import { EventEmitter } from 'events'

import cloneDeep from 'lodash/cloneDeep'
import debounce from 'lodash/debounce'
import findLastIndex from 'lodash/findLastIndex'
import uniqBy from 'lodash/uniqBy'
import twitterText from 'twitter-text'

import type { PostFields } from '@buffer-mono/reminders-config'

import { getMixedMediaCapabilityForService } from '~publish/components/Composer/capability'
import {
  buildDisableDraftTransitionInput,
  buildEnableDraftTransitionInput,
  checkDisableDraftTransitionParity,
  checkEnableDraftTransitionParity,
} from '~publish/components/Composer/store/appSlice/modeTransitionDrift'
import { LinkShorteningDomains } from '~publish/constants/linkShortening'
import {
  DraftMethods,
  getNewDraft,
} from '~publish/legacy/composer/composer/entities/Draft'
import type {
  ChannelDataProperties,
  Draft,
} from '~publish/legacy/composer/composer/entities/Draft'
import Shortener from '~publish/legacy/composer/composer/utils/Shortener'
import {
  MediaAttachmentTypes,
  MediaTypes,
  SERVICE_FACEBOOK,
  SERVICE_GOOGLEBUSINESS,
  SERVICE_INSTAGRAM,
  SERVICE_LINKEDIN,
  SERVICE_MASTODON,
  SERVICE_STARTPAGE,
  SERVICE_THREADS,
  SERVICE_TIKTOK,
  SERVICE_TWITTER,
  SERVICE_YOUTUBE,
} from '~publish/legacy/constants'
import { Service } from '~publish/legacy/constants/services/ServiceDefinitions'
import type { BufferEditor } from '~publish/legacy/editor/BufferEditor/types.plate'
import type { FacebookMentionEntity } from '~publish/legacy/editor/plugins/facebook-mention/nodes'
import { LinkedInAnnotation } from '~publish/legacy/editor/plugins/linkedin-annotations/LinkedInAnnotations'
import type { LinkedInAnnotationEntity } from '~publish/legacy/editor/plugins/linkedin-annotations/nodes'
import { selectShouldShowNBMigration } from '~publish/legacy/organizations/selectors'
import { selectIsIntegrationUploading } from '~publish/legacy/integrations-bar/state/integrationBarSelectors'
import {
  PostTypeCarousel,
  PostTypePost,
  PostTypeReel,
  PostTypeThread,
} from '~publish/legacy/post/constants'
import { getStickersToDisplay } from '~publish/legacy/reminders/components/new-reminders/components/channel-fields/helpers'
import {
  selectPendingCount,
  selectUploaderFiles,
} from '~publish/legacy/uploads/state/selectors'
import { HC_UTM_PARAMS } from '~publish/legacy/utils/contants'

import AppStore from './AppStore'
import { buildThreadedPostReplacement } from './buildThreadedPostReplacement'
import { NotificationStore } from './NotificationStore'
import type {
  ApplyThreadedPostSplitsResult,
  ComposerProfile,
  ThreadedPostSplit,
  ValidationFeedback,
} from './types'
import { getDraftsFeedback } from './utils'
import AppActionCreators from '../action-creators/AppActionCreators'
import ComposerActionCreators from '../action-creators/ComposerActionCreators'
import NotificationActionCreators from '../action-creators/NotificationActionCreators'
import { shortenDraftLink } from '../action-creators/shortenDraftLink'
import {
  AttachmentTypes,
  ErrorTypes,
  NotificationScopes,
  Services,
} from '../AppConstants'
import {
  getYoutubeTitleFromText,
  shouldSyncYoutubeTitleWithText,
} from '../components/youtube/utils'
import AppDispatcher from '../dispatcher'
import type { ComposerImageWorkspaceChange } from '../entities/ComposerImageWorkspaceChange'
import type { Document, DocumentTitle } from '../entities/Document'
import type { Post, Thread } from '../entities/Draft/Draft'
import type { ComposerDispatchPayload } from '../types'
import { EditorStateProxy } from '../entities/EditorStateProxy'
import {
  getNewAvailableImage,
  getNewGif,
  getNewImage,
  getNewLink,
  getNewSourceLink,
  getNewVideo,
} from '../entities/factories'
import type {
  Gif,
  Image,
  Link,
  Media,
  Retweet,
  Video,
  MediaSource,
} from '../entities/factories'
import { MastodonParser } from '../lib/parsers/MastodonParser'
import { validateVideo } from '../lib/validation/ValidateVideo'
import ModalActionCreators from '../shared-components/modal/actionCreators'
import { ActionTypes } from '../state/ActionTypes'
import { getWiredComposerStore } from '../state/getWiredComposerStore'
import { isUrlOnBlocklist } from '../utils/StringUtils'
import { getDebugLogger } from '~publish/legacy/utils/getDebugLogger'

const CHANGE_EVENT = 'change'

// CORE-7763 instrumentation: resolves whether `setDraftEditorState` (the
// onChange-driven writer) ever receives a different `BufferEditor` reference
// than the one already on the draft, or always the same mutated-in-place
// instance. Determines whether the editorState Zustand mirror (CORE-7187) is
// a one-time seed or must re-point on every change. Enable via
// `localStorage.debug = 'publish:composerStore:editorState'`.
const debugEditorState = getDebugLogger('composerStore:editorState')

const store = getWiredComposerStore()

type SynchronousComposerDispatchPayload = {
  action: Omit<ComposerDispatchPayload['action'], 'type'> & {
    actionType: string
  }
}

type ApplyThreadedPostSplitsInput = {
  id: string
  activeThreadId: number
  splits: readonly ThreadedPostSplit[]
}

type AttachmentType = (typeof AttachmentTypes)[keyof typeof AttachmentTypes]
type MediaAttachmentType =
  (typeof MediaAttachmentTypes)[keyof typeof MediaAttachmentTypes]

type ComposerState = {
  drafts: Draft[]
  draftsSharedData: {
    uploadedImages: Image[]
    uploadedGifs: Gif[]
    processingVideos: Map<string, [string, ...string[]]>
    uploadedVideos: Video[]
  }
  meta: {
    lastInteractedWithComposerId: string | null
    activeThreadId: number
    isSuggestedMediaHidden: boolean
    hasMentionsWithoutServer: boolean
    updateTypeChanged: boolean
  }
}

export const getInitialState = (): ComposerState => {
  return {
    get drafts(): Draft[] {
      const drafts = Services.map((service) => getNewDraft(service))
      /*
       * SWC does not have a great support for Circular Dependencies
       * in order to be able to use ComposerStore inside plugins of editor we need to use a getter
       * so that the store.drafts is initialized after the modules initialization
       * This code should be refactored to avoid circular dependencies and to not initialize all drafts at once
       */
      Object.defineProperty(this, 'drafts', {
        value: drafts,
        writable: true,
        enumerable: true,
        configurable: true,
      })

      return drafts
    },
    draftsSharedData: {
      uploadedImages: [],
      uploadedGifs: [],
      processingVideos: new Map(), // uploadId <> [drafId]
      uploadedVideos: [],
    },
    meta: {
      lastInteractedWithComposerId: null,
      activeThreadId: 0,
      isSuggestedMediaHidden: false,
      // used for Mastodon notices
      hasMentionsWithoutServer: false,
      // When the update type is changed automatically.
      // Example: When an IG post is converted into a Reel when it only contains a video.
      updateTypeChanged: false,
    },
  }
}

let state = getInitialState()

const getRequiredDraft = (id: string): Draft => {
  const draft = state.drafts.find((candidate) => candidate.id === id)
  if (!draft) throw new Error(`Draft ${id} was not found`)
  return draft
}

// Keep stable reference to avoid recreating arrays on every call
let lastEnabledDrafts: Draft[] | null = null
let lastEnabledKey: string | null = null
let lastSyncedYoutubeTitlesByDraftId = new Map<string, string>()

// Register with Redux DevTools (uncomment to enable)
// registerStore('composer', state);

const ComposerStore = {
  ...EventEmitter.prototype,
  emitChange: (): boolean => ComposerStore.emit(CHANGE_EVENT),
  resetState: (): void => {
    state = getInitialState()
    // Invalidate the getEnabledDrafts memo so stale Draft references from
    // the prior state don't leak back out after a reset.
    lastEnabledDrafts = null
    lastEnabledKey = null
    lastSyncedYoutubeTitlesByDraftId = new Map()
  },
  addChangeListener: (callback: () => void): EventEmitter =>
    ComposerStore.on(CHANGE_EVENT, callback),
  removeChangeListener: (callback: () => void): EventEmitter =>
    ComposerStore.removeListener(CHANGE_EVENT, callback),

  getEnabledDrafts: (): Draft[] => {
    // Performance optimization: Return stable array reference when enabled state hasn't changed.
    // Without this, .filter() creates a new array on every call, causing React components
    // with useMemo/useEffect dependencies on this array to re-render/recalculate unnecessarily.

    // Create a key representing current enabled state
    const currentEnabledKey = state.drafts
      .map((d) => `${d.id}:${d.isEnabled}`)
      .join(',')

    // Return same array reference if enabled state hasn't changed
    if (lastEnabledDrafts && lastEnabledKey === currentEnabledKey) {
      return lastEnabledDrafts
    }

    // Enabled state changed, create new filtered array
    lastEnabledDrafts = state.drafts.filter((draft) => draft.isEnabled)
    lastEnabledKey = currentEnabledKey

    return lastEnabledDrafts
  },
  getDrafts: (): Draft[] => state.drafts,

  getInvalidEnabledDraftsFeedback: ({
    isDraft = false,
  }: {
    isDraft: boolean
  }): ValidationFeedback[] => {
    const drafts = ComposerStore.getEnabledDrafts()
    const activeThreadId = ComposerStore.getActiveThreadId()

    return getDraftsFeedback({ drafts, activeThreadId, isDraft })
  },

  getIsSuggestedMediaBoxHidden: (): boolean =>
    state.meta.isSuggestedMediaHidden,
  getDraft: (id?: string | null): Draft | undefined =>
    state.drafts.find((draft) => draft.id === id),

  getDraftLinkUrl: (id: string): string | null => {
    const { link } = getRequiredDraft(id)
    return link ? link.url : null
  },

  doesDraftHaveLinkAttachment: (id: string): boolean =>
    getRequiredDraft(id).link !== null,
  doesDraftHaveRetweetAttachment: (id: string): boolean =>
    getRequiredDraft(id).retweet !== null,
  doesDraftHaveLinkAttachmentEnabled: (id: string): boolean =>
    getRequiredDraft(id).enabledAttachmentType === AttachmentTypes.LINK,
  doesDraftHaveSourceLink: (id: string): boolean =>
    getRequiredDraft(id).sourceLink !== null,

  isDraftEnabled: (id: string): boolean => getRequiredDraft(id).isEnabled,
  isDraftLocked: (id: string): boolean => getRequiredDraft(id).isSaved,

  getDraftsSharedData: (): ComposerState['draftsSharedData'] =>
    state.draftsSharedData,

  areAllDraftsSaved: (): boolean => {
    const enabledDrafts = ComposerStore.getEnabledDrafts()
    return (
      enabledDrafts.length > 0 && enabledDrafts.every((draft) => draft.isSaved)
    )
  },

  // Retrieve long link from shortLinkLongLinkMap (if url is long link already, return url)
  getCanonicalUrl: (url: string): string => {
    const matchingDraft = state.drafts.find((draft) =>
      draft.shortLinkLongLinkMap.has(url),
    )

    if (matchingDraft) {
      return matchingDraft.shortLinkLongLinkMap.get(url)
    }

    return url
  },

  // scheduledAt and isPinnedToSlot are the same across all drafts
  getScheduledAt: (): number | null => state.drafts[0].scheduledAt,
  getEnabledDraftScheduledAt: (): number | null => {
    const enabledDrafts = ComposerStore.getEnabledDrafts()
    return enabledDrafts[0]?.scheduledAt
  },
  isSentPost: (): boolean => {
    const { sentPost } = AppStore.getOptions()
    return sentPost
  },
  isPinnedToSlot: (): boolean | null => state.drafts[0].isPinnedToSlot,

  getMeta: (): ComposerState['meta'] => state.meta,

  getSuggestedMediaForDraft: (
    draft: Draft,
    shouldFilterOutAttachedMedia: boolean,
  ): Media[] => {
    if (!DraftMethods.shouldShowSuggestedMedia(draft)) {
      return []
    }

    const enabledDrafts = ComposerStore.getEnabledDrafts()
    const draftsSharedData = ComposerStore.getDraftsSharedData()

    const otherEnabledDrafts = enabledDrafts.filter(
      (enabledDraft) => enabledDraft.id !== draft.id,
    )
    const { availableImages } = draft
    const availableLinkThumbnails =
      draft.link !== null && draft.link.availableThumbnails !== null
        ? draft.link.availableThumbnails
        : []
    const availableSourceLinkImages =
      draft.sourceLink !== null ? draft.sourceLink.availableImages : []

    let suggestedMedia = Array.prototype.concat.call(
      // Other enabled drafts' attached videos
      otherEnabledDrafts.reduce<Video[]>(
        (attachedVideos, otherDraft) =>
          otherDraft.video !== null
            ? attachedVideos.concat(otherDraft.video)
            : attachedVideos,
        [],
      ),
      // Other enabled drafts' attached images
      ...otherEnabledDrafts.map((otherDraft) => otherDraft.images),
      // Other enabled drafts' attached gifs (if service supports gifs)

      draft.service.canHaveMediaAttachmentType(MediaTypes.GIF)
        ? otherEnabledDrafts.reduce<Gif[]>(
            (attachedGifs, otherDraft) =>
              otherDraft.gif !== null
                ? attachedGifs.concat(otherDraft.gif)
                : attachedGifs,
            [],
          )
        : [],
      draftsSharedData.uploadedVideos, // All uploaded videos
      draftsSharedData.uploadedImages, // All uploaded images
      // All uploaded gifs (if service supports gifs)
      draft.service.canHaveMediaAttachmentType(MediaTypes.GIF)
        ? draftsSharedData.uploadedGifs
        : [],
      availableImages, // This draft's available images
      availableLinkThumbnails, // This draft's link attachment's available thumbnails
      availableSourceLinkImages, // Images found on this draft's source url page
    )

    if (shouldFilterOutAttachedMedia) {
      suggestedMedia = suggestedMedia.filter(
        (suggestedItem) =>
          !draft.images.find((image) => suggestedItem.url === image.url) &&
          !draft.videos.some((video) => video.url === suggestedItem.url) &&
          !(draft.gif && draft.gif.url === suggestedItem.url),
      )

      if (draft.hasThread()) {
        const allThreadedMedia = draft.thread.reduce<string[]>(
          (threadedMedia, threadedDraft) => {
            if (threadedDraft.video) {
              threadedMedia.push(threadedDraft.video.url)
            }
            if (threadedDraft.gif) {
              threadedMedia.push(threadedDraft.gif.url)
            }
            if (
              Array.isArray(threadedDraft.images) &&
              threadedDraft.images.length > 0
            ) {
              threadedMedia.push(...threadedDraft.images.map((img) => img.url))
            }

            return threadedMedia
          },
          [],
        )
        suggestedMedia = suggestedMedia.filter(
          (suggestedItem) => !allThreadedMedia.includes(suggestedItem.url),
        )
      }
    }

    // Deduplicate suggested media
    return uniqBy(suggestedMedia, (e) => e.url)
  },

  // Suggested media mixes the user's own uploads/attachments with images
  // scraped from a link or its source page — only the latter carries any
  // rights-to-use uncertainty worth disclaiming.
  hasLinkSuggestedMedia: (draft: Draft): boolean => {
    const availableLinkThumbnails =
      draft.link !== null && draft.link.availableThumbnails !== null
        ? draft.link.availableThumbnails
        : []
    const availableSourceLinkImages =
      draft.sourceLink !== null ? draft.sourceLink.availableImages : []

    return (
      availableLinkThumbnails.length > 0 || availableSourceLinkImages.length > 0
    )
  },

  /**
   * Get the first selected profile for link shortenig config
   *  If omni, use the first selected profile
   *  or first connected profile if no profiles are selected
   *
   * @param { string } draftId
   * @returns {Profile | undefined}
   */
  getFirstSelectedProfile: (draftId: string): ComposerProfile | undefined => {
    const draft = ComposerStore.getDraft(draftId)
    let profiles = []
    if (draft?.service.isOmni) {
      const enabledDrafts = ComposerStore.getEnabledDrafts()
      profiles =
        enabledDrafts.length > 0
          ? AppStore.getSelectedProfilesForService(
              enabledDrafts[0].service.name,
            )
          : AppStore.getProfiles()
    } else {
      profiles = draft?.isEnabled
        ? AppStore.getSelectedProfilesForService(draft.service.name)
        : []
    }

    return profiles.length === 0 ? undefined : profiles[0]
  },

  getActiveThreadId: (): number => state.meta.activeThreadId,
  getNumberOfThreads: (draftId: string): number | undefined => {
    const draft = ComposerStore.getDraft(draftId)

    return draft?.thread?.length
  },

  getPendingUploadsCount: (draft: Draft): number => {
    const storeState = store.getState()
    const uploaderId = DraftMethods.getUploaderId(draft)
    return selectPendingCount(storeState, uploaderId)
  },

  /**
   * Reserved for editor-state operations that must complete in the same event
   * turn to avoid overwriting a newer editor value.
   */
  syncDispatch: (payload: SynchronousComposerDispatchPayload): void => {
    onDispatchedPayload(payload)
  },

  /**
   * Applies generated replacements atomically after revalidating the
   * Composer draft and returns a reason when that snapshot is no longer safe.
   */
  applyThreadedPostSplits: ({
    id,
    activeThreadId,
    splits,
  }: ApplyThreadedPostSplitsInput): ApplyThreadedPostSplitsResult => {
    const result = applyThreadedPostSplits(id, activeThreadId, splits)
    if (result.status === 'applied') ComposerStore.emitChange()

    return result
  },
}

type DraftCallBack<T extends unknown[], R> = (draftId: string, ...args: T) => R

// Should be used for composition with functions that are passed a draft id as first argument.
// It would have been awesome to compose this as a decorator on top of non-class methods,
// alas this isn't part of the spec yet.
function monitorComposerLastInteractedWith<T extends unknown[], R>(
  fn: DraftCallBack<T, R>,
): DraftCallBack<T, R> {
  return (draftId, ...restArgs) => {
    if (
      draftId === 'omni' ||
      draftId === AppStore.getAppState().expandedComposerId
    ) {
      state.meta.lastInteractedWithComposerId = draftId
    }

    return fn(draftId, ...restArgs)
  }
}

const updateDraftHasSavingError = (
  id: string,
  hasSavingError: boolean,
): void => {
  getRequiredDraft(id).hasSavingError = hasSavingError
}

const clearDraftInlineErrors = (id: string): void => {
  const notifications = NotificationStore.getVisibleNotifications().filter(
    (notif) =>
      notif.scope ===
        `${NotificationScopes.UPDATE_SAVING}-${ErrorTypes.INLINE}-${id}` ||
      notif.scope === `${NotificationScopes.PROFILE_QUEUE_LIMIT}-${id}`,
  )
  notifications.forEach((notif) =>
    NotificationActionCreators.removeNotification(notif.id),
  )
}

const enableDraft = monitorComposerLastInteractedWith(
  (id: string, markAppAsLoadedWhenDone: boolean): boolean => {
    let shouldPreventRerender = false

    if (ComposerStore.isDraftLocked(id)) return !shouldPreventRerender

    // CORE-7196 drift-check snapshot: captured before any mutation below,
    // matching deriveEnableDraftTransition's documented pre-enable read.
    // isSoleDraftEmpty is read here too (not later, from the mutable Draft
    // reference above) — copyDraftContents further down can mutate that same
    // draft in place, which would otherwise flip this to a post-mutation value.
    const isOmniboxEnabledBeforeForDriftCheck = AppStore.getIsOmniboxEnabled()
    const enabledDraftsBeforeForDriftCheck = ComposerStore.getEnabledDrafts()
    const isSoleDraftEmptyBeforeForDriftCheck =
      enabledDraftsBeforeForDriftCheck.length === 1 &&
      enabledDraftsBeforeForDriftCheck[0].isEmpty()
    let didExpandJustEnabledDraftForDriftCheck = false

    /**
     * Dashboard-specific rules:
     * - If omnibox is disabled, and there's only a single and empty composer currently
     *   enabled, then enabling a new draft will enable the omnibox, effectively keeping
     *   a single composer visible (the omnibox's)
     * - If omnibox is disabled, and there are multiple or non-empty composers currently
     *   enabled, then enabling a new draft will show an additional composer, which will
     *   be prefilled with the previous composer's contents
     */
    if (!AppStore.getIsOmniboxEnabled()) {
      const enabledDrafts = ComposerStore.getEnabledDrafts()
      const hasOneEmptyEnabledDraft =
        enabledDrafts.length === 1 && enabledDrafts[0].isEmpty()

      if (hasOneEmptyEnabledDraft) {
        AppActionCreators.updateOmniboxState(true)
        shouldPreventRerender = true
      } else if (enabledDrafts.length >= 1) {
        const draftFrom = ComposerStore.getDraft(
          state.meta.lastInteractedWithComposerId,
        )
        // if it's a Thread, we want to preserve editor state to thread structure
        if (draftFrom && draftFrom.hasThread()) {
          draftFrom.thread[state.meta.activeThreadId] =
            getThreadedDraftData(draftFrom)
        }
        copyDraftContents({
          draftFrom,
          draftsTo: [getRequiredDraft(id)],
        })
      }
    }

    getRequiredDraft(id).isEnabled = true

    // When omni is active, copy its content into every channel draft as it is
    // enabled. This handles channel groups where multiple COMPOSER_ENABLE
    // actions fire in rapid succession: each one gets the omni content
    // regardless of how many others are queued at the same time.
    if (AppStore.getIsOmniboxEnabled()) {
      const justEnabledDraft = ComposerStore.getDraft(id)
      if (justEnabledDraft) {
        copyDraftContents({ draftsTo: [justEnabledDraft] })
      }
      // Auto-exit omni whenever the user settles on a single channel. Omni
      // mode only makes sense for 0 channels (empty preview state) or 2+
      // channels (unified editor for many). With exactly 1 channel selected
      // the channel-specific editor is the correct view — regardless of
      // whether omni had content. Multi-channel selection (groups, Select
      // all) keeps the user in the unified omni editor; they can opt into
      // per-channel editing via "Customize for each network".
      // We read `getSelectedProfiles()` rather than `getEnabledDrafts()`
      // because COMPOSER_ENABLE for each group member is queued via setTimeout,
      // so on the first enable `enabledDrafts.length === 1` even when the rest
      // of the group is still pending — `selectedProfiles` is the synchronous
      // source of truth set during the COMPOSER_SELECT_PROFILES forEach.
      if (AppStore.getSelectedProfiles().length === 1) {
        AppActionCreators.updateOmniboxState(null)
        ComposerActionCreators.expand(id)
        didExpandJustEnabledDraftForDriftCheck = true
        shouldPreventRerender = true
      }
    }

    if (markAppAsLoadedWhenDone) AppActionCreators.markAppAsLoaded()

    setValidUpdateType(id)

    // A group/multi-profile select enables each member from within the SAME
    // active COMPOSER_ENABLE dispatch, so this call's own updateOmniboxState
    // above (if it fired) is itself queued behind that dispatch (see
    // AppDispatcher's isDispatching() guard) — reading isOmniboxEnabled
    // synchronously here would see the pre-update value and report false
    // drift. Defer the read past AppDispatcher.whenIdle() so it observes the
    // settled outcome instead.
    const enableDraftDriftInput = buildEnableDraftTransitionInput(
      isOmniboxEnabledBeforeForDriftCheck,
      enabledDraftsBeforeForDriftCheck.length,
      isSoleDraftEmptyBeforeForDriftCheck,
      AppStore.getSelectedProfiles().length,
      AppStore.getIsPreviewMode(),
    )
    void AppDispatcher.whenIdle().then(() => {
      checkEnableDraftTransitionParity(enableDraftDriftInput, {
        isOmniboxEnabledAfter: AppStore.getIsOmniboxEnabled(),
        didExpandJustEnabledDraft: didExpandJustEnabledDraftForDriftCheck,
      })
      return undefined
    })

    return !shouldPreventRerender
  },
)

const disableDraft = (id: string): boolean => {
  let shouldPreventRerender = false

  if (ComposerStore.isDraftLocked(id)) return !shouldPreventRerender

  // CORE-7196 drift-check snapshot: captured before the mutation below,
  // matching deriveDisableDraftTransition's `isOmniboxEnabledBefore` input.
  const isOmniboxEnabledBeforeForDriftCheck = AppStore.getIsOmniboxEnabled()

  getRequiredDraft(id).isEnabled = false

  // Disable omnibox mode if needed
  const enabledDrafts = ComposerStore.getEnabledDrafts()
  const hasOneEnabledDraft = enabledDrafts.length === 1

  if (AppStore.getIsOmniboxEnabled() && hasOneEnabledDraft) {
    AppActionCreators.updateOmniboxState(null)
    copyDraftContents({ draftsTo: enabledDrafts })
    shouldPreventRerender = true
  }

  if (enabledDrafts.length === 0) {
    // create and duplicate treat 0 channels as a valid channel-less draft;
    // editing a real post does not, so it keeps the "select a channel" state.
    const composerMode = AppStore.getOptions().composerMode
    if (composerMode === 'create' || composerMode === 'duplicate') {
      // Sync the omni draft with the most recently active channel draft before
      // re-entering omni mode so the user's latest channel edits are preserved.
      // Prefer the last-interacted-with draft; fall back to the one being disabled.
      const sourceId =
        state.meta.lastInteractedWithComposerId !== 'omni' &&
        state.meta.lastInteractedWithComposerId !== null
          ? state.meta.lastInteractedWithComposerId
          : id
      const sourceDraft = ComposerStore.getDraft(sourceId)
      const omniDraft = ComposerStore.getDraft('omni')
      if (sourceDraft && omniDraft && !sourceDraft.service.isOmni) {
        // The open thread editor is not written into thread[] until the active
        // editor switches, so flush it before copyDraftTextData joins the
        // thread, or the post being edited is dropped.
        const { activeThreadId } = state.meta
        if (
          sourceDraft.hasThread() &&
          activeThreadId >= 0 &&
          activeThreadId < sourceDraft.thread.length
        ) {
          sourceDraft.thread[activeThreadId] = getThreadedDraftData(sourceDraft)
        }
        copyDraftTextData(sourceDraft, omniDraft)
      }
      AppActionCreators.updateOmniboxState(true)
      // Skip the intermediate "0 drafts, omni off" render — the queued
      // omni-on dispatch will trigger the real one. Mirrors the 2→1 branch
      // above.
      shouldPreventRerender = true
    }
  }

  NotificationActionCreators.removeComposerOmniboxNotices(id)

  // Same nested-dispatch deferral as enableDraft's drift check above — this
  // call's own updateOmniboxState (if it fired) may still be queued behind
  // an active COMPOSER_DISABLE dispatch. Defer the read past
  // AppDispatcher.whenIdle() to observe the settled outcome.
  const disableDraftDriftInput = buildDisableDraftTransitionInput(
    isOmniboxEnabledBeforeForDriftCheck,
    enabledDrafts.length,
    AppStore.getOptions().composerMode,
  )
  void AppDispatcher.whenIdle().then(() => {
    checkDisableDraftTransitionParity(
      disableDraftDriftInput,
      AppStore.getIsOmniboxEnabled(),
    )
    return undefined
  })

  return !shouldPreventRerender
}

const getFirstNonNullOrUndefined = <T>(
  ...values: (T | null | undefined)[]
): T | undefined =>
  values.find(
    (value): value is T => typeof value !== 'undefined' && value !== null,
  )

const syncYoutubeTitleWithText = ({
  draft,
  nextText,
  previousText = draft.text,
}: {
  draft: Draft
  nextText: string
  previousText?: string
}): void => {
  if (!draft.service.isYoutube()) {
    return
  }

  const metaData = AppStore.getMetaData()
  const isEditingUpdate = 'updateId' in metaData && metaData.updateId !== null
  if (isEditingUpdate) {
    return
  }

  const youtubeData = draft.channelData?.youtube
  const lastSyncedTitle = lastSyncedYoutubeTitlesByDraftId.get(draft.id)
  if (
    !youtubeData ||
    !shouldSyncYoutubeTitleWithText({
      currentTitle: youtubeData.title,
      lastSyncedTitle,
      previousText,
    })
  ) {
    return
  }

  const nextTitle = getYoutubeTitleFromText(nextText)
  if (youtubeData.title === nextTitle) {
    return
  }

  draft.setChannelData({
    ...youtubeData,
    title: nextTitle,
  })
  lastSyncedYoutubeTitlesByDraftId.set(draft.id, nextTitle)
}

const setDraftInitialText = ({
  id,
  text,
  facebookMentionEntities,
  isEditing,
  annotations,
}: {
  id: string
  text: string
  facebookMentionEntities?: FacebookMentionEntity[]
  isEditing: boolean
  annotations?: LinkedInAnnotationEntity[]
}): void => {
  const draft = ComposerStore.getDraft(id)

  if (!draft) {
    return
  }

  const initialText = text

  // For new YouTube posts, keep the caption intact and default the title to
  // the first caption line unless the user already has a custom title.
  if (draft.service.isYoutube() && !isEditing && initialText !== '') {
    syncYoutubeTitleWithText({ draft, nextText: initialText })
  }

  draft.text = initialText
  draft.editorState = EditorStateProxy.createStateFromText(
    draft.editorState,
    initialText,
    {
      annotations,
      facebookMentionEntities,
    },
  )

  if (draft.service.isInstagram()) {
    ComposerActionCreators.updateDraftCommentCharacterCount(id)
  }

  if (draft.service.isPinterest()) {
    ComposerActionCreators.updateDraftTitleCharacterCount(id)
  }

  ComposerActionCreators.draftUpdated()
}

const setDraftsInitialText = ({
  text,
  facebookMentionEntities,
  isEditing,
  annotations,
}: {
  text: string
  facebookMentionEntities: FacebookMentionEntity[]
  isEditing: boolean
  annotations: LinkedInAnnotationEntity[]
}): void => {
  state.drafts.forEach((draft) =>
    setDraftInitialText({
      id: draft.id,
      text,
      facebookMentionEntities,
      isEditing,
      annotations,
    }),
  )
}

const setDraftEditorState = monitorComposerLastInteractedWith(
  (id: string, editorState: BufferEditor) => {
    const draft = ComposerStore.getDraft(id)

    if (draft) {
      if (draft.editorState && draft.editorState !== editorState) {
        debugEditorState(
          'received a new BufferEditor reference for draft %s',
          id,
        )
      }

      const previousText = draft.text
      const nextText = EditorStateProxy.getPlainText(editorState)

      draft.editorState = editorState
      draft.text = nextText
      syncYoutubeTitleWithText({ draft, nextText, previousText })
    }

    ComposerActionCreators.updateDraftCommentCharacterCount(id)
  },
)

const updateDraftIsSaved = (id: string): void => {
  getRequiredDraft(id).isSaved = true
}

const debouncedUpdateDraftSourceLinkDataActionCreator = debounce(
  (id: string, url: string) => {
    ComposerActionCreators.updateDraftSourceLinkData(id, url)
  },
  250,
)

const updateDraftSourceLink = monitorComposerLastInteractedWith(
  (id: string, url: string | null) => {
    const draft = ComposerStore.getDraft(id)
    if (!draft?.service.canHaveSourceUrl) return

    if (url === '' || url === null) {
      draft.sourceLink = null
    } else {
      draft.sourceLink = getNewSourceLink(url)

      const isValidUrl = twitterText.isValidUrl(url, true, false)
      if (isValidUrl) debouncedUpdateDraftSourceLinkDataActionCreator(id, url)
    }
  },
)

type SourceLinkImage = Pick<Image, 'url' | 'width' | 'height'> &
  Partial<Pick<Image, 'source' | 'altText'>>

type SourceLinkData = {
  url: string
  images?: SourceLinkImage[]
}

const updateDraftSourceLinkData = (
  id: string,
  { url, images = [] }: SourceLinkData,
): void => {
  const draftSourceLink = ComposerStore.getDraft(id)?.sourceLink
  if (!draftSourceLink) return

  Object.assign(draftSourceLink, {
    url,
    availableImages: images.map((image) =>
      getNewImage({
        url: image.url,
        width: image.width,
        height: image.height,
        source: image.source,
        altText: image.altText,
      }),
    ),
  })
}

const updateDraftImageUserTags = monitorComposerLastInteractedWith(
  (id: string, userTags: Image['userTags'], mediaUrl?: string) => {
    const draft = ComposerStore.getDraft(id)

    if (!draft) {
      return
    }

    draft.images?.forEach((image, i) => {
      if (mediaUrl && image.url === mediaUrl) {
        draft.images[i].userTags = userTags
      }
    })
  },
)

const updateDraftTitle = monitorComposerLastInteractedWith(
  (id: string, title: string) => {
    const draft = ComposerStore.getDraft(id)
    if (draft) {
      draft.title = title
    }
  },
)

const updateDraftScheduledAt = monitorComposerLastInteractedWith(
  (
    draftId: string,
    timestamp: number | null,
    isPinnedToSlot: boolean = false,
  ) => {
    const draft = ComposerStore.getDraft(draftId)
    if (draft) {
      draft.scheduledAt = timestamp
      draft.isPinnedToSlot = isPinnedToSlot
    }
  },
)

const updateDraftListPlaces = (id: string, places: Draft['places']): void => {
  const draft = getRequiredDraft(id)
  draft.places = places
}

// `video` selects which attached video to update (mirrors removeDraftVideo's
// index-by-reference lookup); omitted, this targets draft.video (the first)
// for the legacy, non-workspace thumbnail editor, which only ever operates
// on the first video's tile.
const updateVideoDraftThumbnail = (
  id: string,
  thumbOffset: number,
  thumbnail: string,
  video?: Draft['video'],
): void => {
  const draft = getRequiredDraft(id)
  const target = video ?? draft.video
  if (!target || draft.videos.indexOf(target) === -1) return

  target.thumbnail = thumbnail
  // thumbnailOffset is a GraphQL Int on save; round here too so a float
  // never lingers in draft state between the slider and the save mapper.
  target.thumbOffset = Math.round(Number(thumbOffset))
}

const updateToggleSidebarVisibility = monitorComposerLastInteractedWith(
  (id: string, composerSidebarVisible: boolean) => {
    const draft = ComposerStore.getDraft(id)
    if (draft) {
      draft.composerSidebarVisible = composerSidebarVisible
    } else {
      const enabledDrafts = ComposerStore.getEnabledDrafts()

      enabledDrafts.forEach((enabledDraft) => {
        enabledDraft.composerSidebarVisible = composerSidebarVisible
      })
    }
  },
)

const updateRegenerateShortenedLinks = monitorComposerLastInteractedWith(
  (id: string, shouldShortenLinks: boolean) => {
    let draft: Draft | undefined
    if (id) {
      draft = ComposerStore.getDraft(id)
      if (!draft) {
        return
      }
      if (shouldShortenLinks) {
        handleNewDraftLinksAndReshorten(id, draft.urls)
      } else {
        unshortenDraftLinks(id, draft)
      }
    }
  },
)

const updateShouldShortenLinks = monitorComposerLastInteractedWith(
  (id: string, shouldShortenLinks: boolean) => {
    let draft: Draft | undefined
    if (id) {
      draft = ComposerStore.getDraft(id)
      if (!draft) {
        return
      }

      draft.shortenLinksToggle = shouldShortenLinks
    } else {
      const enabledDrafts = ComposerStore.getEnabledDrafts()

      enabledDrafts.forEach((enabledDraft) => {
        enabledDraft.shortenLinksToggle = shouldShortenLinks
      })
    }
  },
)

const hideSuggestedMedia = (): void => {
  state.meta.isSuggestedMediaHidden = true
}

const updateDraftChannelData = monitorComposerLastInteractedWith(
  (id: string, channelData: ChannelDataProperties) => {
    const draft = ComposerStore.getDraft(id)

    if (!draft) {
      return
    }

    draft.setChannelData(channelData)
  },
)

const updateDraftUpdateType = monitorComposerLastInteractedWith(
  (id: string, updateType: string) => {
    const draft = ComposerStore.getDraft(id)
    if (!draft) return

    draft.updateType = updateType

    const capability = getMixedMediaCapabilityForService(draft.service.name)
    if (!capability.canMixMediaTypes) return

    // The combined cap itself can depend on the post type (e.g. Instagram
    // Reels caps combined assets at 1 vs. 10 for a feed post), so recompute
    // it after the switch above — a transition into a stricter post type
    // can leave the draft over its own new limit. Only images are trimmed
    // here; if the video(s)/gif alone already exceed the new cap, they are
    // left in place — there's no safe way to drop one without the user's
    // input.
    const maxCount = DraftMethods.getMaxAttachableAssetsCount(draft)
    const combinedCount =
      DraftMethods.getImageCount(draft) +
      draft.videos.length +
      (draft.gif !== null ? 1 : 0)
    const overflow = combinedCount - maxCount
    if (overflow > 0) draft.truncateImages(overflow)
  },
)

const updateDraftInitialStickers = monitorComposerLastInteractedWith(
  (id: string) => {
    const draft = ComposerStore.getDraft(id)

    if (!draft) {
      return
    }

    draft.selectedStickers = getStickersToDisplay({
      service: draft.id,
      updateType: draft.updateType ?? 'post',
    }).filter((sticker) =>
      Object.keys(draft?.channelData?.[draft.id] ?? {}).includes(sticker),
    )
  },
)

const updateDraftSelectedStickers = monitorComposerLastInteractedWith(
  (id: string, selectedStickers: PostFields[]) => {
    const draft = ComposerStore.getDraft(id)

    if (!draft) {
      return
    }

    draft.selectedStickers = selectedStickers
  },
)

const updateDraftIsReminder = monitorComposerLastInteractedWith(
  (id: string, isReminder: boolean) => {
    const draft = ComposerStore.getDraft(id)
    if (draft?.service.supportsMobileReminders) {
      draft.isReminder = isReminder
    }
  },
)

const updateDraftCommentCharacterCount = monitorComposerLastInteractedWith(
  (id: string) => {
    let draft
    if (id) {
      draft = ComposerStore.getDraft(id)

      if (
        !draft ||
        (draft.service.name !== 'instagram' &&
          draft.service.name !== 'linkedin') ||
        draft.service.commentCharLimit === null
      ) {
        return
      }

      draft.characterCommentCount = DraftMethods.getFullCharacterCount(
        draft,
        draft.getCommentText(),
      )
    } else {
      const enabledDrafts = ComposerStore.getEnabledDrafts()

      enabledDrafts.forEach((enabledDraft) => {
        if (enabledDraft.service.commentCharLimit === null) {
          return
        }

        enabledDraft.characterCommentCount = DraftMethods.getFullCharacterCount(
          enabledDraft,
          enabledDraft.getCommentText(),
        )
      })
    }
  },
)

const updateDraftTitleCharacterCount = monitorComposerLastInteractedWith(
  (id: string) => {
    const draft = ComposerStore.getDraft(id)
    if (draft) {
      if (
        draft.service.name !== 'pinterest' ||
        draft.service.titleCharLimit === null
      )
        return

      draft.characterTitleCount = DraftMethods.getFullCharacterCount(
        draft,
        draft.title ?? '',
      )
    } else {
      const enabledDrafts = ComposerStore.getEnabledDrafts()

      enabledDrafts.forEach((enabledDraft) => {
        if (enabledDraft.service.titleCharLimit === null) {
          return
        }

        enabledDraft.characterTitleCount = DraftMethods.getFullCharacterCount(
          enabledDraft,
          enabledDraft.title ?? '',
        )
      })
    }
  },
)

const mapShortLinkWithLongLink = (
  id: string,
  shortLink: string,
  longLink: string,
): void => {
  const draft = getRequiredDraft(id)
  draft.shortLinkLongLinkMap.set(shortLink, longLink)
}

const unmapShortLinkWithLongLink = (id: string, link: string): void => {
  const draft = getRequiredDraft(id)
  draft.shortLinkLongLinkMap.delete(link)
}

/**
 * Handle all new LinkedIn entity urls that should be converted
 * to annotations.
 *
 * If multiple LinkedIn profiles are selected, we use the id from
 * the first one for the LinkedIn lookup requests.
 *
 * This function is run async as not to block the rest of the editor,
 * but inside it we wait for all lookup requests to complete
 * before starting to edit the draft editor state.
 *
 * We do this because if we start the editing flow while the
 * requests aren't completed yet, we risk ending up with a stale
 * state by the time we push our updates, which would overwrite
 * any changes made to the editor state while we were waiting
 * for the requests to complete.
 */
const handleNewLinkedInEntityUrls = async (
  newUrls: string[],
): Promise<void> => {
  const draftId = Service.Linkedin
  const [profile] = AppStore.getSelectedProfilesForService(draftId)

  if (profile) {
    const entityUrls = await LinkedInAnnotation.parseLinkedInEntitiesUrlInText(
      newUrls,
      profile.id,
    )
    const newEditorState = ComposerStore.getDraft(draftId)?.editorState

    if (entityUrls.length !== 0) {
      if (!newEditorState) return

      let updatedEditorState = newEditorState
      entityUrls.forEach((entityDetails) => {
        updatedEditorState = EditorStateProxy.insertLinkedInAnnotation(
          updatedEditorState,
          entityDetails,
        )
      })

      // At the end, run setDraftEditorState to perform other actions
      setDraftEditorState(draftId, updatedEditorState)
    }
  }
}

/**
 * Loop over each block of text in the editor, and update the list of all urls
 * within the composer + detect new and removed ones
 */
const parseDraftTextLinks = (id: string): void => {
  const draft = getRequiredDraft(id)
  const parsedUrls = EditorStateProxy.parseLinks(draft.editorState)

  const newUrls = parsedUrls.filter((url) => !draft.urls.includes(url))
  const removedUrls = draft.urls.filter((url) => !parsedUrls.includes(url))

  draft.urls = parsedUrls

  if (newUrls.length) {
    handleNewDraftLinks(id, newUrls)
  }

  if (removedUrls.length) {
    handleRemovedDraftLinks(id, removedUrls)
  }

  if (newUrls.length || removedUrls.length) {
    ComposerActionCreators.draftUpdated()
  }
}

/**
 * Used to display notices for links and mentions. We should remove it when we implement
 * autocomplete plugins for Mastodon.
 */
const parseMastodonText = (): void => {
  const draft = getRequiredDraft('mastodon')
  const text = draft.text
  const hasMentionsWithoutServer = MastodonParser.hasMentionsWithoutServer(text)
  state.meta.hasMentionsWithoutServer = hasMentionsWithoutServer
}

const unshortenDraftLinks = (id: string, draft: Draft): void => {
  draft.shortLinkLongLinkMap.forEach((unshortenLink: string) => {
    ComposerActionCreators.draftTextLinkUnshortened(id, unshortenLink)
  })

  const unshortenedUrls: string[] = []

  draft.urls.forEach((url) => {
    replaceDraftLinkWithUnshortenedLink(
      id,
      ComposerStore.getCanonicalUrl(url),
      url,
    )
    unshortenedUrls.push(ComposerStore.getCanonicalUrl(url))
  })

  draft.urls = unshortenedUrls
  draft.unshortenedUrls = unshortenedUrls
}

const handleNewDraftLinksAndReshorten = (
  id: string,
  newUrls: string[],
): void => {
  handleNewDraftLinks(id, newUrls, true)
}

const handleNewDraftLinks = (
  id: string,
  newUrls: string[],
  reShortenLinks = false,
): void => {
  const draft = getRequiredDraft(id)

  // If it's an omni session, we only want to enable link shortening if at
  // least one channel is selected and _all_ selected channels have
  // shortening enabled. With no channel selected we never shorten.
  if (draft?.service.name === 'omni') {
    const selectedProfiles = AppStore.getSelectedProfiles()
    const byDomain = new Map<string, ComposerProfile[]>()
    let total = 0
    for (const profile of selectedProfiles) {
      const existing = byDomain.get(profile.shorteningDomain) ?? []
      byDomain.set(profile.shorteningDomain, [...existing, profile])
      if (profile.shorteningDomain !== LinkShorteningDomains.NoShortening) {
        total++
      }
    }
    // to avoid shortening too many times, we pre-warm the link shortening
    // cache with all of the links that we'll want to shorten for all of the channels (profiles)
    // it's important that we cache per link shortening domain to ensure custom shorteners, etc are
    // respected.
    const noShortening = byDomain.get(LinkShorteningDomains.NoShortening) ?? []
    for (const [domain, profiles] of byDomain) {
      if (
        profiles.length > 1 &&
        domain !== LinkShorteningDomains.NoShortening
      ) {
        // If utm tracking is enabled, we don't want to pre-shorten the links
        // to ensure we get a unique link for each profile
        let toShorten = profiles
          .filter((p) => !p.utmTrackingEnabled)
          .map((p) => p.id)

        // If this is the default shortener, we may as well pre-cache for
        // disabled profiles so that if they toggle on, we'll have the cache ready
        if (domain === LinkShorteningDomains.DefaultShortener) {
          toShorten = [...toShorten, ...noShortening.map((p) => p.id)]
        }
        Shortener.warmCache(toShorten, newUrls, domain)
      }
    }
    draft.shortenLinksToggle =
      selectedProfiles.length > 0 && total === selectedProfiles.length
  }

  const isLinkedInDraft = draft.service.name === Service.Linkedin

  if (isLinkedInDraft) {
    handleNewLinkedInEntityUrls(newUrls)
    // LinkedIn entity urls are already processed
    newUrls = newUrls.filter(
      (url) => !LinkedInAnnotation.isLinkedInEntityUrl(url),
    )
  }

  if (newUrls.length === 0) return

  // If link attachment is disabled update it with first newly-added url
  // (As long as it's not on the blocklist)
  const isBlocked = isUrlOnBlocklist(ComposerStore.getCanonicalUrl(newUrls[0]))
  if (!isBlocked) {
    // If draft does not have any active link attachments
    // process links for attachments.
    if (
      !ComposerStore.doesDraftHaveLinkAttachmentEnabled(id) &&
      draft?.service.canHaveAttachmentType(
        AttachmentTypes.LINK,
        draft?.updateType,
      )
    ) {
      ComposerActionCreators.updateDraftLink(id, newUrls[0])

      // If no attachment is currently added, bring link attachment up with new link
      if (!draft.hasAttachment()) {
        ComposerActionCreators.toggleAttachment(id, AttachmentTypes.LINK)
      }
    }
  } else {
    // If the first URL is blocked, show a notice about blocked URLs.
    NotificationActionCreators.queueInfo({
      scope: NotificationScopes.FB_IG_URL_NO_LINK_PREVIEW,
      message: `Due to recent changes with Facebook's Data Policy, we can no longer generate link previews for <br />
      Facebook and Instagram links.
      <a
        rel="noopener noreferrer"
        target="_blank"
        href="https://support.buffer.com/articles/sharing-facebook-and-instagram-links-through-buffer-qIyKvsGQCc?${HC_UTM_PARAMS}">
        Learn more
      </a>.`,
      isUnique: true,
      onlyCloseOnce: true,
    })
  }

  newUrls.forEach((newUrl) => {
    // Shorten newly-added links if not unshortened before
    if (!draft) {
      return
    }

    const wasUnshortenedBefore = draft.unshortenedUrls.includes(newUrl)
    if (!wasUnshortenedBefore || reShortenLinks) {
      if (
        draft?.shouldShortenLinks() &&
        Shortener.shouldShorten({
          service: draft?.service,
          url: newUrl,
        })
      ) {
        const profile = ComposerStore.getFirstSelectedProfile(id)
        if (profile) shortenDraftLink(id, newUrl, profile)
        updateShouldShowShortLinkMessage(id, false)
      } else {
        if (draft?.shouldShortenLinks() && Shortener.isLinkTooShort(newUrl)) {
          // If the link is too short, we don't want to shorten it, and we want to
          // show a message instead
          updateShouldShowShortLinkMessage(id, true)
        }
      }
    }
    // Add images scraped from new links to list of available images
    ComposerActionCreators.updateDraftLinkAvailableImages(id, newUrl)
  })
}

const handleRemovedDraftLinks = (id: string, removedUrls: string[]): void => {
  const draft = getRequiredDraft(id)

  removedUrls.forEach((removedUrl) => {
    // Remove from available images those whose source link has been removed
    ComposerActionCreators.removeDraftLinkAvailableImages(id, removedUrl)

    // Forget this link was possibly unshortened before to allow shortening it
    // again if re-added
    const unshortenedUrlIndex = draft.unshortenedUrls.indexOf(removedUrl)
    if (unshortenedUrlIndex !== -1)
      draft.unshortenedUrls.splice(unshortenedUrlIndex, 1)

    unmapShortLinkWithLongLink(id, removedUrl)
  })
}

/**
 * One-stop shop for updating draft link data. When a new link attachment is created,
 * if a piece of link attachment data that could be retrieved from the scraper is
 * missing, the scraper will be automatically invoked to retrieve more data.
 *
 * @param {string} id - Draft id
 * @param {object} linkData - Contains any number of link data properties
 * @param {object} meta
 * @param {boolean} meta.isNewLinkAttachment - Whether a new link is being attached,
 *                                             or an existing one being updated
 * @param {boolean} meta.comesFromDirectUserAction - Whether e.g. a keypress led to
 *                                                   linkData being updated
 *
 * The values from both meta.isNewLinkAttachment and meta.comesFromDirectUserAction
 * will determine how linkData is used. If meta.isNewLinkAttachment is true, linkData
 * will replace all previous values, and missing data will be reset to null. If it's
 * false, then linkData will be composed onto the existing link attachment's data.
 * Some properties can be customized by users and are valuable to keep around when new
 * link attachement data is set (e.g. after scraping a link). That's the case of
 * link.title, link.description, and link.thumbnail. When meta.comesFromDirectUserAction
 * is false, these properties will never be overridden by new linkData. When it's true,
 * these properties will always take new values from new linkData (if provided).
 *
 * Example usage:
 *
 * 1. Creating a new empty link attachment:
 *
 *    updateDraftLinkData(id, { url }, {
 *      isNewLinkAttachment: true,
 *      comesFromDirectUserAction: false,
 *    })
 *
 * 2. Updating a link attachment's title when users manually edit it:
 *
 *    updateDraftLinkData(id, { title }, {
 *      isNewLinkAttachment: false,
 *      comesFromDirectUserAction: true,
 *    })
 *
 * 3. Update a link attachment's data after scraping the page:
 *
 *    updateDraftLinkData(id, scrapedData, {
 *      isNewLinkAttachment: false,
 *      comesFromDirectUserAction: false,
 *    })
 */
const updateDraftLinkData = monitorComposerLastInteractedWith(
  (
    id: string,
    linkData: Partial<Link> & { url: string },
    meta: {
      isNewLinkAttachment: boolean
      comesFromDirectUserAction: boolean
    },
  ) => {
    const { isNewLinkAttachment, comesFromDirectUserAction } = meta
    const draft = ComposerStore.getDraft(id)

    if (
      !draft ||
      !draft.service.canHaveAttachmentType(
        AttachmentTypes.LINK,
        draft.updateType,
      )
    ) {
      return
    }

    const {
      url,
      expandedUrl = null,
      title = null,
      description = null,
      thumbnail = null,
      availableThumbnails = null,
      originalThumbnailUrl = undefined,
      scrapingError,
    } = linkData

    /**
     * Facebook Groups are reminders-only, so they don't support link attachments
     */
    const hasFacebookGroupSelected =
      AppStore.getSelectedProfilesForService('facebook').filter(
        (service) => service.serviceType === 'group',
      ).length > 0
    if (hasFacebookGroupSelected) {
      return
    }

    const hasLinkData = draft.link !== null
    const hasAvailableThumbnails =
      availableThumbnails !== null && availableThumbnails.length > 0
    const fallBackToLinkAttachmentData = !isNewLinkAttachment && hasLinkData
    const prioritizeLinkAttachmentData =
      fallBackToLinkAttachmentData && !comesFromDirectUserAction

    // In some situations (e.g. editing an update that has a custom thumbnail in the dashboard),
    // an attached thumbnail won't be found among the scraped availableThumbnails: this flag is used
    // to know when to insert such an attached thumbnail into the collection of availableThumbnails
    // so that navigating between available thumbnails doesn't lose this initial thumbnail.
    const hasUnavailableThumbnailAttached =
      draft.link !== null &&
      draft.link.thumbnail !== null &&
      availableThumbnails !== null &&
      !availableThumbnails.some((thumb: { url: string } | string) => {
        if (typeof thumb === 'string') {
          return thumb === draft.link?.thumbnail?.url
        }
        return thumb.url === draft.link?.thumbnail?.url
      })

    let linkThumbnail = null
    if (draft.link !== null && draft.link.thumbnail !== null) {
      linkThumbnail = originalThumbnailUrl
        ? { ...draft.link.thumbnail, originalUrl: originalThumbnailUrl }
        : draft.link.thumbnail
    }

    draft.link = getNewLink({
      url:
        getFirstNonNullOrUndefined(
          url,
          fallBackToLinkAttachmentData ? draft.link?.url : null,
        ) ?? url,
      expandedUrl: getFirstNonNullOrUndefined(
        expandedUrl,
        fallBackToLinkAttachmentData ? draft.link?.expandedUrl : null,
      ),
      title: getFirstNonNullOrUndefined(
        prioritizeLinkAttachmentData ? draft.link?.title : null,
        title !== null ? title.replace(/\n/g, '') : null, // Strip out line breaks
        fallBackToLinkAttachmentData ? draft.link?.title : null,
      ),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      description: getFirstNonNullOrUndefined(
        prioritizeLinkAttachmentData ? draft.link?.description : null,
        description,
        fallBackToLinkAttachmentData ? draft.link?.description : null,
      ),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      thumbnail: getFirstNonNullOrUndefined(
        thumbnail !== null
          ? typeof thumbnail === 'string'
            ? getNewImage({ url: thumbnail })
            : getNewImage({
                url: thumbnail.url,
                originalUrl: thumbnail.originalUrl,
                width: thumbnail.width,
                height: thumbnail.height,
                source: thumbnail?.source,
                altText: thumbnail?.altText,
              })
          : null,
        fallBackToLinkAttachmentData ? linkThumbnail : null,
        hasAvailableThumbnails
          ? typeof availableThumbnails[0] === 'string'
            ? getNewImage({ url: availableThumbnails[0] })
            : getNewImage({
                url: availableThumbnails[0].url,
                originalUrl: availableThumbnails[0].url,
                width: availableThumbnails[0].width,
                height: availableThumbnails[0].height,
                altText: availableThumbnails[0]?.altText,
                source: availableThumbnails[0]?.source,
              })
          : null,
      ),
      thumbnailHttps: null,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      availableThumbnails: getFirstNonNullOrUndefined(
        availableThumbnails !== null
          ? [
              ...(hasUnavailableThumbnailAttached && draft.link?.thumbnail
                ? [draft.link.thumbnail]
                : []),
              ...availableThumbnails.map((thumb) =>
                typeof thumb === 'string'
                  ? getNewImage({ url: thumb })
                  : getNewImage({
                      url: thumb.url,
                      altText: thumb?.altText,
                      source: thumb?.source,
                      width: thumb.width,
                      height: thumb.height,
                    }),
              ),
            ]
          : null,
        fallBackToLinkAttachmentData ? draft.link?.availableThumbnails : null,
      ),
      scrapingError,
      wasEdited: isNewLinkAttachment
        ? false
        : comesFromDirectUserAction || !!(draft.link && draft.link.wasEdited),
    })

    // If the link is missing some data that we could retrieve by scraping it, do so
    const scrapableProps: (keyof Link)[] = [
      'title',
      'description',
      'availableThumbnails',
    ]
    const isAnyScrapablePropMissing = scrapableProps.some(
      (prop) => draft.link?.[prop] === null,
    )
    if (isAnyScrapablePropMissing) {
      ComposerActionCreators.scrapeDraftLinkData(id, url, originalThumbnailUrl)
    }

    // A failed scrape keeps the attachment: the post is saved with the bare
    // link and the network generates the preview at publish time.
    const { canEdit } = draft.canEditLinkAttachment(
      AppStore.getSelectedProfiles(),
      AppStore.getAppState().domainsOwnedByFacebookPages,
    )
    if (scrapingError && !canEdit) {
      NotificationActionCreators.queueInfo({
        scope: NotificationScopes.LINK_ATTACHMENT_FAILURE_NO_EDIT,
        message:
          "We were unable to generate a preview of this link. This may happen if the website blocks Buffer's access to images and other content. The network will generate the preview when the post is published. <a href='https://support.buffer.com/articles/troubleshooting-issues-with-suggested-media-and-link-details-W3acbamCYl' target='_blank'>Learn more</a>",
        data: {
          service: draft.service.name,
        },
        isUnique: true,
        onlyCloseOnce: false,
      })
    } else if (scrapingError && canEdit) {
      NotificationActionCreators.queueInfo({
        scope: NotificationScopes.LINK_ATTACHMENT_FAILURE,
        message:
          "We were unable to generate a preview of this link. This may happen if the website blocks Buffer's access to images and other content. The network will generate the preview when the post is published, or you can provide the details below. <a href='https://support.buffer.com/articles/troubleshooting-issues-with-suggested-media-and-link-details-W3acbamCYl' target='_blank'>Learn more</a>",
        data: {
          service: draft.service.name,
        },
        isUnique: true,
        onlyCloseOnce: false,
      })
    }

    if (isNewLinkAttachment)
      AppActionCreators.refreshFacebookDomainOwnershipData()
  },
)

const insertAtSelectionOrder = (
  image: Image,
  draft: Draft,
  uploadId?: string,
): void => {
  const replaceTarget =
    shouldReplaceCanvaImage(image, draft.images) ||
    shouldReplaceUploadcareImage(image, draft.images)
  if (replaceTarget) {
    draft.replaceImageAt(draft.images.indexOf(replaceTarget), image)
    return
  }

  const uploaderId = DraftMethods.getUploaderId(draft)
  const uploads = selectUploaderFiles(store.getState(), uploaderId)
  // Prefer matching by upload id since uploadCompleted (which sets url in the
  // uploads slice) runs after handleUploadFinished — the current upload's url
  // isn't in Redux yet when we run. Earlier siblings already finished, so
  // their urls in the slice are valid for the prefix walk below.
  let myIndex = uploadId
    ? uploads.findIndex((upload) => upload.id === uploadId)
    : -1
  if (myIndex === -1) {
    myIndex = uploads.findIndex((upload) => upload.url === image.url)
  }

  if (myIndex === -1) {
    draft.appendImage(image)
    return
  }

  // Count items in draft.images that should precede the new upload:
  // non-upload images always come first; earlier-selected siblings that already
  // landed also come before the new upload.
  const allUploadUrls = new Set(
    uploads.map((u) => u.url).filter((url): url is string => !!url),
  )
  const earlierUploadUrls = new Set(
    uploads
      .slice(0, myIndex)
      .map((u) => u.url)
      .filter((url): url is string => !!url),
  )
  let insertAt = 0
  for (const existing of draft.images) {
    if (
      !allUploadUrls.has(existing.url) ||
      earlierUploadUrls.has(existing.url)
    ) {
      insertAt++
    }
  }
  draft.insertImageAt(insertAt, image)
}

// Returns whether the image was actually attachable to this draft — false
// only when the service/mode can never take an image (a mismatch the pool
// shouldn't keep resurfacing as suggested media). A cap-driven truncation or
// outright rejection still returns true: the file is a valid upload for this
// draft, just not one that fit, and stays recoverable via Suggested Media.
const addDraftImage = monitorComposerLastInteractedWith(
  (
    id: string,
    image: Draft['images'][number],
    uploadId?: string,
    isUploadCompletion = false,
  ): boolean => {
    const draft = ComposerStore.getDraft(id)
    if (!draft) {
      return false
    }
    const hasAttachedVideo = draft.video !== null
    const hasAttachedGif = draft.gif != null

    if (
      !draft.service.canHaveAttachmentType(
        AttachmentTypes.MEDIA,
        draft.updateType,
      )
    )
      return false
    if (!draft.service.canHaveMediaAttachmentType(MediaTypes.IMAGE))
      return false

    const capability = getMixedMediaCapabilityForService(draft.service.name)

    // Check if this is a replacement operation (Canva or UploadCare edit)
    const isReplacement =
      shouldReplaceCanvaImage(image, draft.images) ||
      shouldReplaceUploadcareImage(image, draft.images)

    // Calculate overflow, accounting for replacements (which don't add new images)
    // Note: getPendingUploadsCount includes the current upload being processed,
    // so we subtract 1 for replacements since they don't increase the total count
    const currentCount = DraftMethods.getImageCount(draft)
    const pendingCount = ComposerStore.getPendingUploadsCount(draft)
    // Where mixing is allowed, already-attached video(s)/gif also occupy a
    // slot in the network's combined cap, so they count toward the overflow
    // just like an image would.
    const attachedNonImageCount = capability.canMixMediaTypes
      ? draft.videos.length + (hasAttachedGif ? 1 : 0)
      : 0
    const maxCount = capability.canMixMediaTypes
      ? DraftMethods.getMaxAttachableAssetsCount(draft)
      : draft.service.maxAttachableImagesCount(draft)
    const isStoryReminder =
      draft.service.isInstagram() && draft.isStoryPost() && draft.isReminder
    if (
      isStoryReminder &&
      !isReplacement &&
      draft.images.length +
        draft.videos.length +
        (hasAttachedGif ? 1 : 0) +
        (isUploadCompletion ? 0 : pendingCount) >=
        maxCount
    ) {
      addMediaTruncatedNotice(
        getMediaTruncatedMessage(draft, maxCount),
        draft.id,
      )
      return true
    }
    const newImagesOverflow =
      currentCount +
      pendingCount +
      attachedNonImageCount -
      maxCount -
      (isReplacement ? 1 : 0)
    const needsToMakeRoomForNewImages = newImagesOverflow > 0

    // Truncating existing images can never make room when the already-
    // attached video(s)/gif alone already fill the cap — there's nothing
    // left to remove, so reject the new image outright instead of exceeding
    // the network's combined limit.
    if (
      capability.canMixMediaTypes &&
      !isReplacement &&
      attachedNonImageCount >= maxCount
    ) {
      addMediaTruncatedNotice(
        getMediaTruncatedMessage(draft, maxCount),
        draft.id,
      )
      return true
    }

    if (needsToMakeRoomForNewImages && !isStoryReminder) {
      draft.truncateImages(newImagesOverflow)
      addMediaTruncatedNotice(
        getMediaTruncatedMessage(draft, maxCount),
        draft.id,
      )
    }
    // The channel-less omni draft doesn't publish anything itself — it's a
    // staging area copied onto each real per-channel draft at Customize time
    // (copyDraftContents), which is where canMixMediaTypes actually gets
    // enforced. Clearing here too would mean a mixed-media org loses its
    // image the instant a video attaches, if this fires before the org's
    // mixed-media flag has resolved (its default reads incapable) — the
    // upload happens right after composer opens, while a later Customize
    // click has had much longer for the flag to settle.
    if (!capability.canMixMediaTypes && !draft.service.isOmni) {
      const overriddenLabels: string[] = []
      if (hasAttachedVideo) {
        draft.setVideo(null) // Override video
        overriddenLabels.push('video')
      }
      if (hasAttachedGif) {
        draft.setGif(null) // Override gif
        overriddenLabels.push('GIF')
      }
      if (overriddenLabels.length > 0) {
        addMediaTruncatedNotice(
          getMediaTypeOverriddenMessage(draft, overriddenLabels),
          draft.id,
        )
      }
    }
    if (DraftMethods.hasLinkAttachment(draft)) {
      enableDraftAttachment(draft.id, AttachmentTypes.MEDIA)
    }

    // Note that this needs to mutates draft.images directly.
    // Inserts at the slot dictated by selection order from the uploads slice
    // (which preserves addFile order) so fast uploads that finish ahead of
    // earlier-selected siblings still end up in the correct position.
    insertAtSelectionOrder(image, draft, uploadId)
    ComposerActionCreators.draftImageAdded(id, image.url)

    return true
  },
)

const updateDraftVideoThumbnail = monitorComposerLastInteractedWith(
  (id: string, thumbnail: string) => {
    const draft = ComposerStore.getDraft(id)
    if (!draft?.service.canEditVideoAttachment || !draft.video) return

    draft.video.thumbnail = thumbnail
    draft.video.wasEdited = true
  },
)

const updateDraftVideoTitle = monitorComposerLastInteractedWith(
  (id: string, title: string, video?: Draft['video']) => {
    const draft = ComposerStore.getDraft(id)
    // The omni draft has no capabilities of its own; the workspace offers a
    // title only when a selected channel accepts one, and the title reaches
    // the channel drafts when omni content is copied onto them.
    if (
      !draft ||
      (!draft.service.isOmni &&
        !draft.service.canEditVideoAttachment &&
        !draft.service.canEditVideoTitle)
    ) {
      return
    }

    const target = video ?? draft.video
    if (!target || draft.videos.indexOf(target) === -1) return

    target.name = title
    target.wasEdited = true
  },
)

const updateDraftLinkThumbnail = monitorComposerLastInteractedWith(
  (id: string, thumbnail: Image) => {
    const draft = ComposerStore.getDraft(id)

    if (!draft?.link) {
      return
    }

    draft.link.thumbnail = thumbnail
    draft.link.thumbnailHttps = thumbnail.url

    // TODO: merge updateDraftLinkThumbnail() into updateDraftLinkData()
    draft.link.wasEdited = true
    draft.link.scrapingError = undefined
  },
)

const updateShouldShowShortLinkMessage = monitorComposerLastInteractedWith(
  (id: string, shouldShowShortLinkMessage: boolean) => {
    const draft = ComposerStore.getDraft(id)
    if (!draft) {
      return
    }
    draft.showShortLinkMessage = shouldShowShortLinkMessage
  },
)

const selectNextLinkThumbnail = monitorComposerLastInteractedWith(
  (draftId: string) => {
    const draft = ComposerStore.getDraft(draftId)
    const currentThumbnail = draft?.link?.thumbnail
    const availableThumbnails = draft?.link?.availableThumbnails
    if (!currentThumbnail || !availableThumbnails) return

    const thumbnails = [...availableThumbnails]
    const currThumbnailIndex = findLastIndex(
      thumbnails,
      (thumbnail) =>
        (typeof thumbnail === 'string' ? thumbnail : thumbnail.url) ===
        currentThumbnail.url,
    )
    const nextThumbnail =
      currThumbnailIndex === thumbnails.length - 1
        ? thumbnails[0]
        : thumbnails[currThumbnailIndex + 1]

    if (!nextThumbnail) return

    ComposerActionCreators.updateDraftLinkThumbnail(
      draftId,
      typeof nextThumbnail === 'string'
        ? getNewImage({ url: nextThumbnail })
        : nextThumbnail,
    )
  },
)

const selectPreviousLinkThumbnail = monitorComposerLastInteractedWith(
  (draftId: string) => {
    const draft = ComposerStore.getDraft(draftId)
    const currentThumbnail = draft?.link?.thumbnail
    const availableThumbnails = draft?.link?.availableThumbnails
    if (!currentThumbnail || !availableThumbnails) return

    const thumbnails = [...availableThumbnails]
    const currThumbnailIndex = findLastIndex(
      thumbnails,
      (thumbnail) =>
        (typeof thumbnail === 'string' ? thumbnail : thumbnail.url) ===
        currentThumbnail.url,
    )
    const prevThumbnail =
      currThumbnailIndex <= 0
        ? thumbnails[thumbnails.length - 1]
        : thumbnails[currThumbnailIndex - 1]

    if (!prevThumbnail) return

    ComposerActionCreators.updateDraftLinkThumbnail(
      draftId,
      typeof prevThumbnail === 'string'
        ? getNewImage({ url: prevThumbnail })
        : prevThumbnail,
    )
  },
)

const addDraftLinkAvailableThumbnail = (id: string, thumbnail: Image): void => {
  const draft = getRequiredDraft(id)
  if (!draft.link) return
  const availableThumbnails = draft.link.availableThumbnails
  if (availableThumbnails === null) {
    draft.link.availableThumbnails = [thumbnail]
  } else {
    draft.link.availableThumbnails = [
      thumbnail,
      ...availableThumbnails.map((item) =>
        typeof item === 'string' ? getNewImage({ url: item }) : item,
      ),
    ]
  }
}

const addDraftUploadedLinkThumbnail = (
  id: string,
  url: string,
  width: number | null,
  height: number | null,
  source?: MediaSource | null,
): void => {
  const draftsSharedData = ComposerStore.getDraftsSharedData()
  const formattedImage = getNewImage({ url, width, height, source })

  /**
   * It's important for the three collections below to share the same formattedImage
   * reference, so that a change made in one location propagates to all other places
   * seamlessly.
   * TODO: Re-implement this logic in an immutable fashion: it'll be a bit more work
   * to manually search and update all places where an image can be stored, but it'll
   * be much more solid.
   */
  updateDraftLinkThumbnail(id, formattedImage)
  addDraftLinkAvailableThumbnail(id, formattedImage)
  draftsSharedData.uploadedImages.push(formattedImage)
  state.meta.isSuggestedMediaHidden = false
}

/**
 * Adds or replaces an uploaded image to the provided collection, depending on whether
 * the image comes from Canva or UploadCare and an existing image from the same source
 * is found in the collection (replace so that you get the feeling of editing the image),
 * otherwise appends the new image.
 *
 * TODO: Refactor to not rely on reference mutations
 */
const addOrReplaceDraftUploadedImage = (
  image: Image,
  uploadedImages: Image[],
): void => {
  const prevCanvaImage = shouldReplaceCanvaImage(image, uploadedImages)
  const prevUploadcareImage = shouldReplaceUploadcareImage(
    image,
    uploadedImages,
  )
  const prevImage = prevCanvaImage || prevUploadcareImage

  if (prevImage) {
    const index = uploadedImages.indexOf(prevImage)
    uploadedImages[index] = image
  } else {
    uploadedImages.push(image)
  }
}

const shouldReplaceCanvaImage = (
  image: Image,
  collection: Image[],
): Image | undefined => {
  if (image.source?.name === 'canva') {
    return collection.find((item) => item.source?.id === image.source?.id)
  }
  return undefined
}

const shouldReplaceUploadcareImage = (
  image: Image,
  collection: Image[],
): Image | undefined => {
  if (image.source?.name === 'uploadcare' && image.source?.id) {
    return collection.find((item) => item.url === image.source?.id)
  }
  return undefined
}

const addDraftUploadedImage = (args: {
  draftId: string
  url: string
  width: number
  height: number
  name?: string
  size?: number
  source?: MediaSource | null
  uploadId?: string
}): void => {
  const { draftId, url, width, height, name, size, source, uploadId } = args
  const draftsSharedData = ComposerStore.getDraftsSharedData()
  const formattedImage = getNewImage({ url, width, height, name, size, source })

  /**
   * It's important for the two collections below to share the same formattedImage
   * reference, so that a change made in one location propagates to all other places
   * seamlessly.
   * TODO: Re-implement this logic in an immutable fashion: it'll be a bit more work
   * to manually search and update all places where an image can be stored, but it'll
   * be much more solid.
   */
  const wasAttachable = addDraftImage(draftId, formattedImage, uploadId, true)

  // A draft whose service/mode can never take an image (e.g. an incompatible
  // update type) never actually gets this image — don't let it resurface as
  // suggested media for a file that was never a real attachment.
  if (wasAttachable) {
    addOrReplaceDraftUploadedImage(
      formattedImage,
      draftsSharedData.uploadedImages,
    )
  }
}

const addAutoUploadedImage = (
  url: string,
  altText: string | null | undefined,
  source: MediaSource | null | undefined,
  height: number | null,
  width: number | null,
): void => {
  const draftsSharedData = ComposerStore.getDraftsSharedData()
  const formattedImage = getNewImage({ url, altText, source, height, width })

  /**
   * It's important for the two collections below to share the same formattedImage
   * reference, so that a change made in one location propagates to all other places
   * seamlessly.
   * TODO: Re-implement this logic in an immutable fashion: it'll be a bit more work
   * to manually search and update all places where an image can be stored, but it'll
   * be much more solid.
   */
  draftsSharedData.uploadedImages.push(formattedImage)
  state.drafts.forEach((draft) => addDraftImage(draft.id, formattedImage))
}

const handleMediaRemovalCleanup = (draft: Draft): void => {
  // Clear any UPDATE_SAVING notifications for this service
  clearDraftInlineErrors(draft.service.name)

  // Trigger a refresh of the full composer
  ComposerActionCreators.draftUpdated()
}

const removeDraftImage = monitorComposerLastInteractedWith(
  (id: string, image: Image) => {
    const draft = ComposerStore.getDraft(id)
    const imageIndex = draft?.images?.indexOf(image)

    if (!draft || imageIndex === undefined || imageIndex === -1) return

    draft.removeImageAt(imageIndex)

    handleMediaRemovalCleanup(draft)
  },
)

// TODO: Refactor to not rely on reference mutations
const updateImageAltText = (
  image: Image,
  altText: string | null | undefined,
): void => {
  image.altText = altText
}

const createChangedWorkspaceImage = (
  image: Image,
  { altText, userTags, replacement }: ComposerImageWorkspaceChange,
): Image => ({
  ...image,
  ...(replacement
    ? {
        url: replacement.url,
        width: replacement.width,
        height: replacement.height,
        name: replacement.name,
        size: replacement.size,
        source: replacement.source,
      }
    : {}),
  altText,
  userTags:
    userTags.length > 0
      ? userTags.map(({ username, x, y }) => ({ username, x, y }))
      : undefined,
})

const applyImageWorkspaceChanges = monitorComposerLastInteractedWith(
  (draftId: string, changes: readonly ComposerImageWorkspaceChange[]): void => {
    const draft = ComposerStore.getDraft(draftId)

    if (!draft || changes.some(({ image }) => !draft.images.includes(image))) {
      return
    }

    const changesByImage = new Map(
      changes.map((change) => [change.image, change]),
    )
    draft.setImages(
      draft.images.map((image) => {
        const change = changesByImage.get(image)
        return change ? createChangedWorkspaceImage(image, change) : image
      }),
    )
  },
)

// Swaps one block of images for another inside a single store update, so
// anything watching the draft (validation, media limits, previews) never
// observes a transient count or order. The new block takes the position of
// the first replaced image; when none of the old URLs are present the new
// images append at the end.
const replaceDraftImages = monitorComposerLastInteractedWith(
  (draftId: string, oldUrls: readonly string[], images: Image[]): void => {
    const draft = ComposerStore.getDraft(draftId)
    if (!draft || images.length === 0) return
    const oldUrlSet = new Set(oldUrls)
    const startIndex = draft.images.findIndex((image) =>
      oldUrlSet.has(image.url),
    )
    const nextImages = draft.images.filter((image) => !oldUrlSet.has(image.url))
    nextImages.splice(
      startIndex === -1 ? nextImages.length : startIndex,
      0,
      ...images,
    )
    draft.setImages(nextImages)
  },
)

const setImageBeingReplaced = ({
  imageUrl,
  isBeingReplaced,
  serviceId,
}: {
  imageUrl: string
  isBeingReplaced: boolean
  serviceId: Service | 'omni'
}): void => {
  const draft = ComposerStore.getDraft(serviceId)
  if (!draft) return

  if (isBeingReplaced) {
    draft.imagesBeingReplaced.add(imageUrl)
  } else {
    draft.imagesBeingReplaced.delete(imageUrl)
  }
}

/**
 * TODO: Refactor to not rely on reference mutations
 * This method is currently only called when auto-uploading images (i.e. auto-attached
 * images on app load), so the two collections where we're sure to be able to find
 * them at any point in time are draftsSharedData.uploadedImages/uploadedGifs.
 * Find the image there and mutate it. This is quite brittle, hence the todo above.
 */
const updateUploadedImageDimensions = (
  url: string,
  width: number,
  height: number,
): void => {
  const draftsSharedData = ComposerStore.getDraftsSharedData()
  const collections = [
    draftsSharedData.uploadedImages,
    draftsSharedData.uploadedGifs,
  ]
  let image: Image | Gif | undefined

  collections.some((imageCollection) => {
    image = imageCollection.find((uploadedImage) => uploadedImage.url === url)
    return !!image
  })

  if (image) {
    image.width = width
    image.height = height
  }
}

/**
 * Note: When adding to the draft a video that already exists within the app, it's
 * important to pass the video object's reference around rather than creating a new
 * equivalent video object, since those references are compared in different places
 * using === to establish if we're looking at the same video or not.
 */
// Returns whether the video was actually attachable to this draft — false
// only when the service/mode can never take a video. Callers use this to
// decide whether the upload belongs in the shared suggested-media pool: a
// video that could never attach anywhere shouldn't resurface as suggested.
const addDraftVideo = monitorComposerLastInteractedWith(
  (id: string, video: Draft['video'], isUploadCompletion = false): boolean => {
    const draft = ComposerStore.getDraft(id)

    if (!draft) return false

    const hasAttachedImages = draft.images?.length && draft.images.length > 0
    const hasAttachedVideo = draft.video !== null
    const hasAttachedGif = draft.gif !== null

    if (
      !draft.service.canHaveAttachmentType(
        AttachmentTypes.MEDIA,
        draft.updateType,
      )
    )
      return false
    if (!draft.service.canHaveMediaAttachmentType(MediaTypes.VIDEO))
      return false

    const capability = getMixedMediaCapabilityForService(draft.service.name)

    if (
      draft.service.isInstagram() &&
      draft.isStoryPost() &&
      draft.isReminder &&
      video !== null &&
      draft.images.length +
        draft.videos.length +
        (hasAttachedGif ? 1 : 0) +
        (isUploadCompletion
          ? 0
          : ComposerStore.getPendingUploadsCount(draft)) >=
        DraftMethods.getMaxAttachableAssetsCount(draft)
    ) {
      addMediaTruncatedNotice(
        getMediaTruncatedMessage(
          draft,
          DraftMethods.getMaxAttachableAssetsCount(draft),
        ),
        draft.id,
      )
      return true
    }

    // See the matching comment in addDraftImage — the omni draft stages
    // media for every channel at once and never publishes itself, so it must
    // never lose a type here regardless of what canMixMediaTypes reads.
    if (!capability.canMixMediaTypes && !draft.service.isOmni) {
      const overriddenLabels: string[] = []
      if (hasAttachedImages) {
        draft.clearImages() // Override images
        overriddenLabels.push('image')
      }
      if (hasAttachedGif) {
        draft.setGif(null) // Override gif
        overriddenLabels.push('GIF')
      }
      if (overriddenLabels.length > 0) {
        addMediaTruncatedNotice(
          getMediaTypeOverriddenMessage(draft, overriddenLabels),
          draft.id,
        )
      }
    }

    // A bare video auto-converts a feed post to a Reel (setValidUpdateType,
    // called at the end of this function) — correct for exactly one video,
    // but a Reel's cap of 1 asset (see ServiceDefinitions'
    // maxAttachableAssetsCount) would otherwise permanently block a second
    // video from ever appending: the cap check below runs against whatever
    // updateType this draft currently holds. Revert to a feed post first
    // whenever this video would make it a genuine multi-video attachment,
    // so the cap check evaluates against the feed cap instead of the
    // Reel's — setValidUpdateType only converts back to Reel if this draft
    // still ends up with a single video after this update.
    if (
      draft.updateType === PostTypeReel &&
      capability.canHaveMultipleVideos &&
      hasAttachedVideo
    ) {
      draft.updateType = PostTypePost
    }

    // Multiple videos are only additive when the capability allows it, a
    // video is already attached, and the network's combined cap (e.g. an
    // Instagram Reel's cap of 1) still has room for another video — images
    // can be truncated to make room, but nothing can be truncated to protect
    // a cap that video count alone already fills, so appending falls back
    // to the single-slot replace path in that case instead of exceeding it.
    //
    // The omni draft's own canHaveMultipleVideos reads the org flag directly
    // (see getMixedMediaCapabilityForService), which defaults to false until
    // the flag resolves — a second video uploaded quickly after the composer
    // opens would otherwise replace the first before that resolves. Unlike a
    // real per-channel draft, omni has no single network's cap to protect
    // here (a channel-less draft never publishes against one, and Customize
    // re-evaluates per channel against its own resolved capability), so it
    // always appends.
    const willAppendVideo =
      (capability.canHaveMultipleVideos || draft.service.isOmni) &&
      hasAttachedVideo &&
      video !== null &&
      draft.videos.length < DraftMethods.getMaxAttachableAssetsCount(draft)

    if (capability.canMixMediaTypes) {
      // Post-op video count: the existing videos plus the incoming one when
      // appending, or just the incoming one when replacing (any prior video
      // is cleared below in that case).
      const videoCountAfterOp = willAppendVideo ? draft.videos.length + 1 : 1
      const combinedCount =
        DraftMethods.getImageCount(draft) +
        (draft.gif !== null ? 1 : 0) +
        videoCountAfterOp
      const maxCount = DraftMethods.getMaxAttachableAssetsCount(draft)
      const overflow = combinedCount - maxCount
      if (overflow > 0) {
        draft.truncateImages(overflow)
        addMediaTruncatedNotice(
          getMediaTruncatedMessage(draft, maxCount),
          draft.id,
        )
      }
    }

    if (willAppendVideo) {
      draft.appendVideo(cloneDeep(video))
    } else {
      if (hasAttachedVideo) draft.setVideo(null) // Override video
      draft.setVideo(cloneDeep(video))
    }

    ComposerActionCreators.draftVideoAdded(id, video)

    setValidUpdateType(id)

    return true
  },
)

/**
 * Update the update type if not valid.
 */
const setValidUpdateType = (draftId: string): void => {
  const draft = ComposerStore.getDraft(draftId)
  if (!draft) return

  const hasVideoAttachment = draft.hasVideoAttachment()
  const isInstagramPost = draft.service?.isInstagram()
  const isFeedPost = draft.updateType === PostTypePost
  const capability = getMixedMediaCapabilityForService(draft.service.name)

  // A Reel can't coexist with other attached media, so only convert when
  // the video is the sole attachment — a video alongside images or a gif
  // is a mixed carousel, not a bare-video Reel candidate, and neither is a
  // second video: a Reel's cap of 1 asset (see ServiceDefinitions'
  // maxAttachableAssetsCount) can't hold two. That exception only applies
  // where mixing is actually allowed; otherwise any video attachment
  // converts the post, as it always has.
  //
  // getUploaderId bakes the post type into the uploader's own id, so
  // flipping to Reel here swaps the composer to a brand-new Uploader/Uppy
  // instance (see useComposerUploader/useUploader) — which tears down and
  // aborts whatever the old instance was still mid-upload. A multi-file
  // selection uploads every file through the SAME instance at once, so the
  // first of several videos to finish would otherwise convert the draft to
  // a Reel before its siblings ever land, killing them in flight. Hold off
  // converting while more than this one upload is still pending for the
  // draft's current uploader id — the last upload to land re-runs this
  // check (every add* path calls setValidUpdateType), so a genuine single
  // video still converts once nothing else is left in flight.
  if (
    isInstagramPost &&
    hasVideoAttachment &&
    isFeedPost &&
    draft.videos.length <= 1 &&
    ComposerStore.getPendingUploadsCount(draft) <= 1 &&
    (!capability.canMixMediaTypes ||
      (!draft.hasImagesAttachment() && !draft.hasGifAttachment()))
  ) {
    // Calls the store function directly (not the dispatched action creator):
    // this runs synchronously inside an in-progress dispatch (e.g. a
    // channel-copy loop), and AppDispatcher defers a reentrant dispatch via
    // setTimeout. A deferred conversion would apply against whatever media
    // the draft holds by the time the timeout fires, which can by then
    // include an image added after this decision was made — truncating it
    // to fit the Reel cap this stale conversion is about to impose.
    updateDraftUpdateType(draftId, PostTypeReel)
    state.meta.updateTypeChanged = true
  }
}

// Restores every video of an already-existing, already-valid post (editing
// an existing post, or a thread segment's saved media) in one shot, rather
// than replaying them one at a time through addDraftVideo's reactive
// append-vs-replace/Reel-conversion logic. That logic decides whether to
// append based on the org's *current* mixed-media capability — live-gating
// existing content this way is wrong (the flag governs new attachments, not
// content that already got saved with N videos) and racy (the capability
// may not have resolved yet when a freshly-opened composer loads a post),
// so a video could load, decide the capability reads false, and be silently
// replaced by the next one. Setting every video directly first means
// setValidUpdateType's own `videos.length <= 1` guard always sees the true,
// final count.
const loadDraftVideos = (draftId: string, videos: readonly Video[]): void => {
  if (videos.length === 0) return

  const draft = ComposerStore.getDraft(draftId)
  if (!draft) return

  if (
    !draft.service.canHaveAttachmentType(
      AttachmentTypes.MEDIA,
      draft.updateType,
    )
  )
    return
  if (!draft.service.canHaveMediaAttachmentType(MediaTypes.VIDEO)) return

  draft.setVideo(cloneDeep(videos[0]))
  videos.slice(1).forEach((video) => draft.appendVideo(cloneDeep(video)))

  setValidUpdateType(draftId)
}

const addSharedUploadedVideo = (videoData: Video): void => {
  const draftsSharedData = ComposerStore.getDraftsSharedData()
  draftsSharedData.uploadedVideos.push(videoData)
}

// `video` selects one entry out of a multi-video draft (mirrors
// removeDraftImage's index-by-reference lookup); omitted, this clears every
// video, matching every single-video caller's existing expectation.
const removeDraftVideo = monitorComposerLastInteractedWith(
  (id: string, video?: Draft['video']) => {
    const draft = ComposerStore.getDraft(id)
    if (!draft) return

    if (video) {
      const videoIndex = draft.videos.indexOf(video)
      if (videoIndex === -1) return
      draft.removeVideoAt(videoIndex)
    } else {
      draft.setVideo(null)
    }

    handleMediaRemovalCleanup(draft)
  },
)

const removeDraftDocument = monitorComposerLastInteractedWith((id: string) => {
  const draft = ComposerStore.getDraft(id)
  if (!draft) return

  draft.setDocument(null)
  if (draft?.service.isLinkedin()) {
    DraftMethods.setUpdateType(draft, PostTypePost)
  }
})

// Returns whether the gif was actually attachable to this draft — false
// only when the service/mode can never take a gif, or there's no gif to
// attach. A cap-driven truncation or outright rejection still returns true,
// matching addDraftImage/addDraftVideo: the pool keeps it recoverable.
const addDraftGif = monitorComposerLastInteractedWith(
  (id: string, gif: Draft['gif']): boolean => {
    const draft = ComposerStore.getDraft(id)

    if (!draft || !gif) return false

    const hasAttachedImages = draft.images?.length > 0
    const hasAttachedVideo = draft.video !== null

    if (
      !draft.service.canHaveAttachmentType(
        AttachmentTypes.MEDIA,
        draft.updateType,
      )
    )
      return false
    if (!draft.service.canHaveMediaAttachmentType(MediaTypes.GIF)) return false

    const capability = getMixedMediaCapabilityForService(draft.service.name)

    // See the matching comment in addDraftImage — the omni draft stages
    // media for every channel at once and never publishes itself, so it must
    // never lose a type here regardless of what canMixMediaTypes reads.
    if (!capability.canMixMediaTypes && !draft.service.isOmni) {
      const overriddenLabels: string[] = []
      if (hasAttachedImages) {
        draft.clearImages() // Override images
        overriddenLabels.push('image')
      }
      if (hasAttachedVideo) {
        draft.setVideo(null) // Override video
        overriddenLabels.push('video')
      }
      if (overriddenLabels.length > 0) {
        addMediaTruncatedNotice(
          getMediaTypeOverriddenMessage(draft, overriddenLabels),
          draft.id,
        )
      }
    }

    if (capability.canMixMediaTypes) {
      const maxCount = DraftMethods.getMaxAttachableAssetsCount(draft)

      // Truncating images can never make room when the already-attached
      // video(s) alone already fill the cap — reject the gif outright
      // instead of exceeding the network's combined limit.
      if (draft.videos.length >= maxCount) {
        addMediaTruncatedNotice(
          getMediaTruncatedMessage(draft, maxCount),
          draft.id,
        )
        return true
      }

      // The gif itself always contributes exactly one slot post-op (new
      // attach or replace), so the combined count only needs the images and
      // videos still standing after the clears above.
      const combinedCount =
        DraftMethods.getImageCount(draft) + draft.videos.length + 1
      const overflow = combinedCount - maxCount
      if (overflow > 0) {
        draft.truncateImages(overflow)
        addMediaTruncatedNotice(
          getMediaTruncatedMessage(draft, maxCount),
          draft.id,
        )
      }
    }

    draft.setGif(gif)

    ComposerActionCreators.draftGifAdded(id, gif.url)

    setValidUpdateType(id)

    return true
  },
)

const enableLinkedInDocument = (draft: Draft): void => {
  if (!draft.service.isLinkedin()) {
    return
  }

  // ensure the document object exists
  if (!draft.document)
    draft.setDocument({ mediaType: MediaTypes.DOCUMENT, title: '' })

  // remove any other media
  if (draft.images?.length) draft.clearImages()
  if (draft.video) draft.setVideo(null)

  DraftMethods.setUpdateType(draft, PostTypeCarousel)
}

const addDraftDocument = monitorComposerLastInteractedWith(
  (id: string, document: Document | DocumentTitle) => {
    const draft = ComposerStore.getDraft(id)

    if (!draft?.service.canHaveMediaAttachmentType(MediaAttachmentTypes.PDF)) {
      return
    }

    enableDraftAttachment(id, AttachmentTypes.MEDIA)
    enableLinkedInDocument(draft)

    // preserve title
    if (draft.document?.title) document.title = draft.document.title

    draft.setDocument(document)
  },
)

const addDraftUploadedGif = (
  draftId: string,
  url: string,
  stillGifUrl: string,
  width: number | null,
  height: number | null,
  frameCount: number | null,
): void => {
  const draftsSharedData = ComposerStore.getDraftsSharedData()
  const formattedGif = getNewGif(url, stillGifUrl, width, height, frameCount)

  /**
   * It's important for the two collections below to share the same formattedGif
   * reference, so that a change made in one location propagates to all other places
   * seamlessly.
   * TODO: Re-implement this logic in an immutable fashion: it'll be a bit more work
   * to manually search and update all places where an image can be stored, but it'll
   * be much more solid.
   */
  const wasAttachable = addDraftGif(draftId, formattedGif)

  // Mirrors addDraftUploadedImage: a gif that could never attach to this
  // draft shouldn't linger in the pool as a phantom suggestion.
  if (wasAttachable) {
    draftsSharedData.uploadedGifs.push(formattedGif)
  }
}

const addAutoUploadedGif = (url: string, stillGifUrl: string): void => {
  const draftsSharedData = ComposerStore.getDraftsSharedData()
  const formattedGif = getNewGif(url, stillGifUrl)

  /**
   * It's important for the two collections below to share the same formattedGif
   * reference, so that a change made in one location propagates to all other places
   * seamlessly.
   * TODO: Re-implement this logic in an immutable fashion: it'll be a bit more work
   * to manually search and update all places where an image can be stored, but it'll
   * be much more solid.
   */
  draftsSharedData.uploadedGifs.push(formattedGif)
  state.drafts.forEach((draft) => addDraftGif(draft.id, formattedGif))
}

const removeDraftGif = monitorComposerLastInteractedWith((id: string) => {
  const draft = ComposerStore.getDraft(id)
  if (!draft) return

  draft.setGif(null)

  handleMediaRemovalCleanup(draft)
})

const replaceDraftGif = monitorComposerLastInteractedWith(
  (draftId: string, expectedGif: Gif, replacement: Gif): boolean => {
    const draft = ComposerStore.getDraft(draftId)

    if (!draft?.gif || draft.gif !== expectedGif) return false

    draft.setGif(replacement)
    return true
  },
)

const getThreadedDraftData = (draft: Draft, text?: string): Post => {
  return {
    text: text || draft.text,
    get characterCount(): number {
      return DraftMethods.getFullCharacterCount(draft, this.text)
    },
    images: [...draft.images],
    video: draft.video,
    videos: [...draft.videos],
    gif: draft.gif,
    link: draft.link,
    retweet: draft.retweet,
    urls: draft.urls,
    unshortenedUrls: draft.unshortenedUrls,
    enabledAttachmentType: draft.enabledAttachmentType,
  }
}

const getNewThreadedDraftData = (draft: Draft, text = ''): Post => {
  return {
    text,
    get characterCount(): number {
      return DraftMethods.getFullCharacterCount(draft, this.text)
    },
    images: [],
    video: null,
    videos: [],
    gif: null,
    link: null,
    retweet: null,
    enabledAttachmentType: null,
    urls: [],
    unshortenedUrls: [],
  }
}

const updateDraftPostText = (id: string, text: string): void => {
  const draft = ComposerStore.getDraft(id)

  if (draft) {
    const previousText = draft.text
    draft.text = text
    syncYoutubeTitleWithText({ draft, nextText: text, previousText })
  }
}

const updateDraftAddPostToThread = monitorComposerLastInteractedWith(
  (id: string, offset: number) => {
    const draft = ComposerStore.getDraft(id)

    if (!draft) {
      return
    }

    if (selectShouldShowNBMigration(store.getState())) {
      ModalActionCreators.openModal('ThreadsMPPaywall')
      return
    }

    const { hasErrors, shouldJumpToNextThreadedPost } = draft.canAddToThread(
      state.meta.activeThreadId,
    )

    if (shouldJumpToNextThreadedPost) {
      ComposerActionCreators.switchActiveThreadEditor(
        draft.id,
        state.meta.activeThreadId + 1,
      )

      return
    }

    if (hasErrors) {
      return
    }

    if (draft.updateType !== PostTypeThread) {
      draft.updateType = PostTypeThread
    }

    if (!Array.isArray(draft.thread)) {
      draft.thread = []
    } else {
      if (
        draft.service.maxThreads &&
        draft.thread.length >= draft.service.maxThreads
      ) {
        return
      }
    }

    const text = draft.text
    const postOneText = offset ? text.slice(0, offset).trim() : text
    const postTwoText = offset ? text.slice(offset).trim() : ''

    // preserve last active draft
    draft.thread[state.meta.activeThreadId] = getThreadedDraftData(
      draft,
      postOneText,
    )

    // create a new draft entity
    const indexToInsert = state.meta.activeThreadId + 1
    const newDraft = getNewThreadedDraftData(draft, postTwoText)

    draft.thread.splice(indexToInsert, 0, newDraft)

    // remove currently uploaded media
    draft.text = ''
    draft.clearImages()
    draft.setVideo(null)
    draft.setGif(null)
    draft.link = null
    draft.retweet = null
    draft.urls = []
    draft.unshortenedUrls = []
    draft.enabledAttachmentType = AttachmentTypes.MEDIA

    // replace editor with empty text
    setDraftEditorState(
      id,
      EditorStateProxy.createStateFromText(draft.editorState, newDraft.text, {
        annotations: [],
        facebookMentionEntities: [],
      }),
    )

    // Clear editor history to prevent undo from attempting to restore
    // incompatible editor state from before the thread was added
    EditorStateProxy.clearEditorHistory(draft.editorState)

    // set active thread id
    setActiveThreadId(indexToInsert)
    EditorStateProxy.focus(draft.editorState)
  },
)

const applyThreadedPostSplits = monitorComposerLastInteractedWith(
  (
    id: string,
    activeThreadId: number,
    splits: readonly ThreadedPostSplit[],
  ): ApplyThreadedPostSplitsResult => {
    const draft = ComposerStore.getDraft(id)
    const currentActiveThreadId = draft?.hasThread()
      ? state.meta.activeThreadId
      : 0

    if (
      !draft ||
      !draft.service.supportsThread(draft.updateType) ||
      currentActiveThreadId !== activeThreadId
    ) {
      return { status: 'rejected', reason: 'stale' }
    }

    // The open editor holds the active item; thread[] only receives it when
    // the active editor changes, so the active item reads from the draft.
    const activePost = getThreadedDraftData(draft)
    const thread = draft.hasThread()
      ? draft.thread.map((post, index) =>
          index === activeThreadId ? activePost : post,
        )
      : [activePost]

    if (activeThreadId < 0 || activeThreadId >= thread.length) {
      return { status: 'rejected', reason: 'stale' }
    }

    const threadIds = new Set(splits.map(({ threadId }) => threadId))
    if (splits.length === 0 || threadIds.size !== splits.length) {
      return { status: 'rejected', reason: 'invalidSegments' }
    }

    for (const { threadId, sourceText, segments } of splits) {
      const post = thread[threadId]
      if (!post || post.text !== sourceText) {
        return { status: 'rejected', reason: 'stale' }
      }
      if (segments.length < 2 || segments.join('') !== sourceText) {
        return { status: 'rejected', reason: 'invalidSegments' }
      }
    }

    const characterLimit = DraftMethods.getCharLimit(draft)
    if (
      characterLimit === null ||
      splits.some(({ segments }) =>
        segments.some(
          (segment) =>
            DraftMethods.getFullCharacterCount(draft, segment) > characterLimit,
        ),
      )
    ) {
      return { status: 'rejected', reason: 'invalidSegments' }
    }

    if (
      ComposerStore.getPendingUploadsCount(draft) > 0 ||
      selectIsIntegrationUploading(store.getState())
    ) {
      return { status: 'rejected', reason: 'uploadsInProgress' }
    }

    if (selectShouldShowNBMigration(store.getState())) {
      ModalActionCreators.openModal('ThreadsMPPaywall', {})
      return { status: 'rejected', reason: 'paywalled' }
    }

    const defaultAttachmentType = draft.service.canHaveAttachmentType(
      AttachmentTypes.MEDIA,
      draft.updateType,
    )
      ? AttachmentTypes.MEDIA
      : null

    const replacements = new Map(
      splits.map(({ threadId, segments }) => [
        threadId,
        buildThreadedPostReplacement({
          activePost: thread[threadId],
          segments,
          createPost: (text) => {
            const post = getNewThreadedDraftData(draft, text)
            post.enabledAttachmentType = defaultAttachmentType
            return post
          },
          getCharacterCount: (text) =>
            DraftMethods.getFullCharacterCount(draft, text),
          getCanonicalUrl: ComposerStore.getCanonicalUrl,
        }),
      ]),
    )
    const nextThread = thread.flatMap(
      (post, index) => replacements.get(index) ?? [post],
    )

    if (
      draft.service.maxThreads &&
      nextThread.length > draft.service.maxThreads
    ) {
      return { status: 'rejected', reason: 'threadCapacityExceeded' }
    }

    // The editor stays on the item the user was writing: the last segment of
    // the active item when it was split, otherwise the same item at its
    // shifted position.
    const itemsBeforeActive = thread
      .slice(0, activeThreadId)
      .reduce(
        (length, _post, index) =>
          length + (replacements.get(index)?.length ?? 1),
        0,
      )
    const nextActiveThreadId =
      itemsBeforeActive + (replacements.get(activeThreadId)?.length ?? 1) - 1

    draft.thread = nextThread
    draft.updateType = PostTypeThread
    replaceEditorContents(id, draft, nextActiveThreadId)

    // replaceEditorContents clears the draft's URL state and relies on an
    // editor change to re-parse it; that change never happens here, so the
    // now-active item's URLs must be restored for validation to see them.
    const nextActivePost = nextThread[nextActiveThreadId]
    draft.urls = [...nextActivePost.urls]
    draft.unshortenedUrls = [...nextActivePost.unshortenedUrls]

    return { status: 'applied' }
  },
)

const updateAiAssisted = monitorComposerLastInteractedWith((id: string) => {
  const draft = ComposerStore.getDraft(id)
  if (draft) {
    draft.aiAssisted = true
  }
})

const updateDraftThread = monitorComposerLastInteractedWith(
  (id: string, thread: Thread): void => {
    const draft = ComposerStore.getDraft(id)

    if (draft && thread) {
      if (draft.service.supportsThread(draft.updateType)) {
        draft.thread = thread
        draft.updateType = PostTypeThread

        // Clear current media from main draft editor
        draft.clearImages()
        draft.link = null
        draft.setGif(null)
        draft.setVideo(null)
        draft.retweet = null
        draft.urls = []
        draft.unshortenedUrls = []

        // Set up the first thread's content in the main editor
        if (thread.length > 0) {
          const firstThread = thread[0]

          // Set editor text to the first thread's text
          setDraftEditorState(
            id,
            EditorStateProxy.createStateFromText(
              draft.editorState,
              firstThread.text,
              {
                annotations: [],
                facebookMentionEntities: [],
              },
            ),
          )

          // Clear editor history to prevent undo from attempting to restore
          // incompatible editor state from before the thread was loaded
          EditorStateProxy.clearEditorHistory(draft.editorState)

          // Copy media from first thread to main draft editor
          if (
            Array.isArray(firstThread.images) &&
            firstThread.images.length > 0
          ) {
            firstThread.images.forEach((image) => {
              const formattedImage = getNewImage({
                url: image.url,
                source: image.source,
                altText: image.altText,
                width: image.width,
                height: image.height,
                name: image.name,
                size: image.size,
              })
              addDraftImage(id, formattedImage)
            })
          }

          loadDraftVideos(id, firstThread.videos)

          if (firstThread.gif) {
            addDraftGif(id, firstThread.gif)
          }

          if (firstThread.link) {
            draft.link = firstThread.link
          }

          if (firstThread.retweet) {
            draft.retweet = firstThread.retweet
          }

          draft.enabledAttachmentType = firstThread.enabledAttachmentType
        }
      } else {
        // Service doesn't support threads - merge all threaded text into single text
        const joinedThreadsText = thread
          .reduce<string[]>((threadedDraftsText, threadedDraft) => {
            threadedDraftsText.push(threadedDraft.text)
            if (
              threadedDraft?.retweet?.text &&
              threadedDraft.retweet.text.trim() !== ''
            ) {
              threadedDraftsText.push(threadedDraft.retweet.text)
            }
            return threadedDraftsText
          }, [])
          .join('\n')

        setDraftEditorState(
          id,
          EditorStateProxy.createStateFromText(
            draft.editorState,
            joinedThreadsText,
            {
              annotations: [],
              facebookMentionEntities: [],
            },
          ),
        )
        // Clear editor history to prevent undo from attempting to restore
        // incompatible editor state from before threads were merged
        EditorStateProxy.clearEditorHistory(draft.editorState)
        // Don't set thread property since service doesn't support it
        draft.thread = null
        draft.updateType = null
      }
    }
  },
)

// dragIndex/hoverIndex are positions in draft.assets (every attached media
// kind, in tray order) — a single-kind draft's assets are just its images,
// so this is unchanged for callers that never mix media.
const updateDraftMediaOrder = (
  id: string,
  dragIndex: number,
  hoverIndex: number,
): void => {
  const draft = ComposerStore.getDraft(id)
  if (!draft) {
    return
  }

  draft.reorderAssetAt(dragIndex, hoverIndex)
}

const updateDraftThreadOrder = (id: string, newThreadOrder: Post[]): void => {
  const draft = ComposerStore.getDraft(id)
  if (!draft || !Array.isArray(draft.thread)) {
    return
  }

  // Update the draft with the new thread order
  draft.thread = newThreadOrder
}

const deleteThreadedDraft = (
  id: string,
  threadId?: number,
  mergeThreads?: boolean,
): void => {
  const draft = ComposerStore.getDraft(id)

  if (!draft || !Array.isArray(draft.thread)) {
    return
  }

  const { activeThreadId } = state.meta
  const threadToDelete = threadId || activeThreadId

  if (mergeThreads) {
    // If merging threads is requested but a post contains other media than text,
    // skip deleting the thread as it would result in losing the media
    if (draft.images?.length || draft.video || draft.gif || draft.link) {
      return
    }

    draft.thread[activeThreadId] = getThreadedDraftData(draft)

    if (threadToDelete > 0 && draft.thread[threadToDelete].text) {
      draft.thread[threadToDelete - 1].text = `${
        draft.thread[threadToDelete - 1].text
      }
${draft.thread[threadToDelete].text}`
    }
  }

  draft.thread.splice(threadToDelete, 1)

  replaceEditorContents(
    id,
    draft,
    threadToDelete === 0 ? 0 : threadToDelete - 1,
  )

  if (draft.thread.length === 1) {
    draft.thread = null
    draft.updateType = null
  }
}

const setActiveThreadId = (threadId: number): void => {
  state.meta.activeThreadId = threadId
}

const replaceEditorContents = (
  id: string,
  draft: Draft,
  threadId: number,
): void => {
  if (Array.isArray(draft.thread) && draft.thread.length > 0) {
    // reset media available in editor
    draft.clearImages()
    draft.link = null
    draft.setGif(null)
    draft.setVideo(null)
    draft.retweet = null
    draft.urls = []
    draft.unshortenedUrls = []

    // fetch draft we switch to
    const newDraft = draft.thread[threadId]

    draft.text = newDraft.text

    // replace editor text
    setDraftEditorState(
      id,
      EditorStateProxy.createStateFromText(draft.editorState, newDraft.text, {
        annotations: [],
        facebookMentionEntities: [],
      }),
    )

    // Clear editor history to prevent undo from attempting to restore
    // incompatible editor state from before the thread operation
    EditorStateProxy.clearEditorHistory(draft.editorState)

    // add images if available
    if (Array.isArray(newDraft.images) && newDraft.images.length > 0) {
      newDraft.images.forEach((image) => {
        const formattedImage = getNewImage({
          url: image.url,
          source: image.source,
          altText: image.altText,
          width: image.width,
          height: image.height,
          name: image.name,
          size: image.size,
        })
        addDraftImage(id, formattedImage)
      })
    }
    // add videos if available, in order — addDraftVideo appends rather than
    // replaces from the 2nd call on when the capability allows it, mirroring
    // how images are restored above.
    newDraft.videos.forEach((video) => {
      addDraftVideo(id, video)
    })
    // add gif if available
    if (newDraft.gif) {
      addDraftGif(id, newDraft.gif)
    }
    // add link if available
    if (newDraft.link) {
      draft.link = newDraft.link
    }
    if (newDraft.retweet) {
      draft.retweet = newDraft.retweet
    }
    draft.enabledAttachmentType = newDraft.enabledAttachmentType
    setActiveThreadId(threadId)
    ComposerActionCreators.draftUpdated()

    // EditorStateProxy.focus defers to the next animation frame and guards
    // against the editor not being mounted, so the new editor state can be
    // focused safely without a synchronous DOM-resolution race.
    EditorStateProxy.focus(draft.editorState)
  }
}

const switchActiveThreadEditor = (id: string, threadId: number): void => {
  const draft = ComposerStore.getDraft(id)
  const currentlyActiveThreadId = state.meta.activeThreadId

  if (draft?.hasThread()) {
    // preserve current composer data in thread structure
    draft.thread[currentlyActiveThreadId] = getThreadedDraftData(draft)
    replaceEditorContents(id, draft, threadId)
  }
}

const addDraftRetweet = (id: string, retweetData: Retweet): void => {
  const draft = ComposerStore.getDraft(id)
  if (
    !draft?.service.canHaveAttachmentType(
      AttachmentTypes.RETWEET,
      draft.updateType,
    )
  )
    return
  draft.retweet = retweetData

  ComposerActionCreators.draftUpdated()
}

const disableDraftsAttachment = monitorComposerLastInteractedWith(
  (id: string, attachmentType: AttachmentType) => {
    const draft = ComposerStore.getDraft(id)
    if (!draft) return

    draft.enabledAttachmentType = AttachmentTypes.MEDIA

    if (attachmentType === AttachmentTypes.LINK) {
      draft.link = null
    }

    ComposerActionCreators.attachmentToggled(id)
  },
)

const disableDraftAttachment = (id: string): void => {
  const draft = getRequiredDraft(id)
  if (
    draft.service.canHaveAttachmentType(AttachmentTypes.MEDIA, draft.updateType)
  ) {
    draft.enabledAttachmentType = AttachmentTypes.MEDIA
  } else {
    draft.enabledAttachmentType = null
  }

  ComposerActionCreators.attachmentToggled(id)
}

const enableDraftAttachment = (
  id: string,
  attachmentType: AttachmentType,
): void => {
  const draft = getRequiredDraft(id)

  if (!draft.service.canHaveAttachmentType(attachmentType, draft?.updateType))
    return

  // Link Attachment doesn't have a suggestion mode: don't enable it if no link attached
  if (
    attachmentType === AttachmentTypes.LINK &&
    !ComposerStore.doesDraftHaveLinkAttachment(id)
  ) {
    return
  }

  // Retweet Attachment doesn't have a suggestion mode: don't enable it if no retweet attached
  if (
    attachmentType === AttachmentTypes.RETWEET &&
    !ComposerStore.doesDraftHaveRetweetAttachment(id)
  ) {
    return
  }

  // TikTok doesn't have a suggestion mode: don't enable it if service is TikTok
  if (id === 'tiktok') {
    return
  }

  draft.enabledAttachmentType = attachmentType

  ComposerActionCreators.attachmentToggled(id)
}

const toggleDraftAttachment = monitorComposerLastInteractedWith(
  (id: string, attachmentType: AttachmentType) => {
    const draft = ComposerStore.getDraft(id)
    if (!draft) return

    const shouldDisableAttachment =
      draft.enabledAttachmentType === attachmentType

    if (shouldDisableAttachment) {
      disableDraftAttachment(id)
    } else {
      enableDraftAttachment(id, attachmentType)
    }
  },
)

const replaceDraftLinkWithShortlink = (
  id: string,
  link: string,
  shortLink: string,
): void => {
  const draft = getRequiredDraft(id)
  const { editorState } = draft

  draft.editorState = EditorStateProxy.replaceLinkWithShortLink(
    editorState,
    link,
    shortLink,
  )

  ComposerActionCreators.parseDraftTextLinks(id)
}

const addDraftUnshortenedLink = (id: string, unshortenedLink: string): void => {
  const draft = ComposerStore.getDraft(id)
  if (!draft) {
    return
  }

  if (!draft.unshortenedUrls.includes(unshortenedLink)) {
    draft.unshortenedUrls.push(unshortenedLink)
  }
}

const addDraftAvailableImages = (
  id: string,
  images: Image[],
  sourceLink: string,
): void => {
  const draft = getRequiredDraft(id)

  const newAvailableImages = images.map((image) => {
    const formattedImage = getNewImage({
      url: image.url,
      altText: image?.altText,
      source: image?.source,
      width: image?.width,
      height: image?.height,
    })

    return getNewAvailableImage(formattedImage, sourceLink)
  })

  draft.availableImages = draft.availableImages.concat(newAvailableImages)
}

const removeDraftAvailableImages = (id: string, sourceLink: string): void => {
  const draft = getRequiredDraft(id)

  draft.availableImages = draft.availableImages.filter(
    (availableImage) => availableImage.sourceLink !== sourceLink,
  )
}

const addOmniNotice = (message: string, draftId: string): void => {
  NotificationActionCreators.queueInfo({
    scope: NotificationScopes.MC_OMNIBOX_EDIT_NOTICE,
    message,
    onlyCloseOnce: false,
    data: { id: draftId, type: 'notice' },
  })
}

/**
 * Truncated assets stay in draftsSharedData's uploaded-media pool and
 * resurface as Suggested Media rather than being lost — this notice is what
 * makes that recoverable instead of confusing.
 */
const getMediaTruncatedMessage = (draft: Draft, maxAssets: number): string => {
  const assetWord = maxAssets === 1 ? 'file' : 'files'
  return `Only ${maxAssets} ${assetWord} could be attached to ${draft.service.formattedName}. The rest are available in Suggested Media.`
}

/**
 * A network outside MIXED_MEDIA_CAPABLE_SERVICES — or any network while the
 * org-level flag is off — can only hold one media family at a time, so
 * attaching one type silently swaps out another. This is what tells the
 * user why their earlier upload disappeared.
 */
const getMediaTypeOverriddenMessage = (
  draft: Draft,
  removedLabels: string[],
): string => {
  const removed = removedLabels.join(' and ')
  const pronoun = removedLabels.length > 1 ? "They're" : "It's"
  return `${draft.service.formattedName} doesn't support mixing media types, so we removed the previously attached ${removed}. ${pronoun} still available in Suggested Media.`
}

/**
 * Unlike addOmniNotice's MC_OMNIBOX_EDIT_NOTICE (only surfaced while its
 * composer is collapsed), truncation happens on the composer the user is
 * actively looking at, so this needs to show while expanded too — scoped
 * per draft id so one draft's notice doesn't bleed into another's.
 * Batch adds (multi-file upload, draft-to-draft copy) truncate once per
 * file; isUnique collapses those repeats into a single visible notice.
 */
const addMediaTruncatedNotice = (message: string, draftId: string): void => {
  NotificationActionCreators.queueInfo({
    scope: `${NotificationScopes.MEDIA_TRUNCATED}-${draftId}`,
    message,
    onlyCloseOnce: false,
    isUnique: true,
  })
}

const getEditedImagesMessage = (draft: Draft): string => {
  let message
  const maxAttachableImagesCount = draft.service.maxAttachableImagesCount(draft)
  if (maxAttachableImagesCount === 1) {
    message = `${draft.service.formattedName} does not support multiple images, so we've used your first image`
  } else {
    message = `${draft.service.formattedName} allows ${maxAttachableImagesCount}
    images, so we kept the first ${maxAttachableImagesCount} images`
  }
  return message
}

/**
 * Copies draft media
 * @param draftFromMedia -> simplified Draft entity containing only media:
 * {
 *   enabledAttachmentType: string
 *   retweet: Retweet,
 *   video: Video,
 *   gif: Gif,
 *   images: Image[],
 * }
 * @param draftTo -> full entity from Draft.ts
 */
type DraftMedia = {
  enabledAttachmentType: string | null
  link: Link | null
  retweet: Retweet | null
  videos: Video[]
  gif: Gif | null
  images: Image[]
  document: Document | DocumentTitle | null
}

const copyDraftMedia = (draftFromMedia: DraftMedia, draftTo: Draft): void => {
  if (draftFromMedia.images.length > 0) {
    if (!draftTo.service.canHaveMediaAttachmentType(MediaTypes.IMAGE)) {
      const message = `We can't publish images to ${draftTo.service.formattedName}, so we've removed it.`
      addOmniNotice(message, draftTo.id)
    } else {
      let imagesToAttach = draftFromMedia.images
      if (
        draftFromMedia.images.length >
        draftTo.service.maxAttachableImagesCount(draftTo)
      ) {
        imagesToAttach = draftFromMedia.images.slice(
          0,
          draftTo.service.maxAttachableImagesCount(draftTo),
        )
        const message = getEditedImagesMessage(draftTo)
        addOmniNotice(message, draftTo.id)
      }
      imagesToAttach.forEach((image) => addDraftImage(draftTo.id, image))
      enableDraftAttachment(draftTo.id, AttachmentTypes.MEDIA)
    }
  }

  if (draftFromMedia.gif !== null) {
    if (!draftTo.service.canHaveMediaAttachmentType(MediaTypes.GIF)) {
      const message = `We can't publish GIFs to ${draftTo.service.formattedName}, so we've removed it.`
      addOmniNotice(message, draftTo.id)
    } else {
      addDraftGif(draftTo.id, draftFromMedia.gif)
      enableDraftAttachment(draftTo.id, AttachmentTypes.MEDIA)
    }
  }

  if (draftFromMedia.videos.length > 0) {
    if (!draftTo.service.canHaveMediaAttachmentType(MediaTypes.VIDEO)) {
      const message = `We can't publish videos to ${draftTo.service.formattedName}, so we've removed it.`
      addOmniNotice(message, draftTo.id)
    } else {
      draftFromMedia.videos.forEach((video) => {
        let canAttachVideo = true

        // Re-validate TikTok videos before attaching
        if (draftTo.service.name === 'tiktok') {
          if (video.url) {
            const extension = video.url
              .split(/[#?]/)[0]
              .split('.')
              .pop()
              ?.trim()
              .toUpperCase()

            const mediaType = Object.values(MediaAttachmentTypes).find(
              (type): type is MediaAttachmentType => type === extension,
            )

            if (
              mediaType &&
              !draftTo.service.canHaveMediaAttachmentType(mediaType)
            ) {
              const message = `We can't publish videos of this file type to ${draftTo.service.formattedName}, so we've removed it.`
              addOmniNotice(message, draftTo.id)
              canAttachVideo = false
            }
          }

          if (canAttachVideo) {
            const validationResultVideo = validateVideo(video, draftTo.service)

            if (validationResultVideo.isValidationFail()) {
              let { message } = validationResultVideo
              message += ", so we've removed it."
              addOmniNotice(message, draftTo.id)
              canAttachVideo = false
            }
          }
        }

        if (canAttachVideo) {
          addDraftVideo(draftTo.id, video)
          enableDraftAttachment(draftTo.id, AttachmentTypes.MEDIA)
        }
      })
    }
  }

  if (draftFromMedia.document !== null) {
    if (!draftTo.service.canHaveMediaAttachmentType(MediaAttachmentTypes.PDF)) {
      const message = `We can't publish documents to ${draftTo.service.formattedName}, so we've removed it.`
      addOmniNotice(message, draftTo.id)
    } else {
      addDraftDocument(draftTo.id, draftFromMedia.document)
    }
  }
}

const replaceDraftLinkWithUnshortenedLink = (
  id: string,
  unshortenedLink: string,
  shortLink: string,
): void => {
  const draft = getRequiredDraft(id)
  const { editorState } = draft

  draft.editorState = EditorStateProxy.replaceLinkWithUnshortenedLink(
    editorState,
    unshortenedLink,
    shortLink,
  )

  ComposerActionCreators.parseDraftTextLinks(id)
}

const copyDraftTextData = (draftFrom: Draft, draftTo: Draft): void => {
  let newEditorState = EditorStateProxy.copyDraftEditorState(draftFrom, draftTo)

  // A retweet carries over as an attachment when the target service supports
  // retweets; only services without retweet support get the retweet flattened
  // into the editor text.
  const retweetCarriesOverAsAttachment =
    draftFrom.enabledAttachmentType === AttachmentTypes.RETWEET &&
    draftTo.service.canHaveAttachmentType(
      AttachmentTypes.RETWEET,
      draftTo.updateType,
    )

  if (
    !retweetCarriesOverAsAttachment &&
    draftFrom.retweet &&
    draftFrom.retweet.text &&
    draftFrom.retweet.text.trim() !== ''
  ) {
    newEditorState = EditorStateProxy.createStateFromText(
      newEditorState,
      `${draftFrom.text}\n${draftFrom.retweet.text}`,
    )
  }

  if (draftFrom.hasThread()) {
    if (draftTo.service.supportsThread(draftTo.updateType)) {
      newEditorState = EditorStateProxy.createStateFromText(
        newEditorState,
        draftFrom.thread[0].text,
      )
      draftTo.thread = cloneDeep(draftFrom.thread)
      draftTo.updateType = PostTypeThread
    } else {
      // If we copy from Thread to non-Thread, we want to join all threaded updates
      const joinedThreadsText = DraftMethods.getJoinedThreadsText(draftFrom)
      newEditorState = EditorStateProxy.createStateFromText(
        newEditorState,
        joinedThreadsText,
      )
    }
  }

  setDraftEditorState(draftTo.id, newEditorState)

  const unshortenedUrls: string[] = []
  if (
    (draftTo.service.isPinterest() || draftTo.service.isMastodon()) &&
    draftFrom?.urls.length > 0
  ) {
    draftFrom.urls.forEach((url) => {
      replaceDraftLinkWithUnshortenedLink(
        draftTo.id,
        ComposerStore.getCanonicalUrl(url),
        url,
      )
      unshortenedUrls.push(ComposerStore.getCanonicalUrl(url))
    })
    draftFrom.urls = unshortenedUrls
    draftFrom.unshortenedUrls = unshortenedUrls
  }

  draftTo.urls = draftFrom.urls
  draftTo.unshortenedUrls = draftFrom.unshortenedUrls
  draftTo.shortLinkLongLinkMap = draftFrom.shortLinkLongLinkMap
}

const canEnableLinkAttachmentOnDraftCopy = (
  draftFrom: Draft,
  draftTo: Draft,
): boolean => {
  if (!draftTo.urls || draftTo.urls.length === 0 || !draftTo.urls[0]) {
    return false
  }

  const draftFromCanHaveLink = draftFrom.service.canHaveAttachmentType(
    AttachmentTypes.LINK,
    draftFrom.updateType,
  )

  const draftToCanHaveLink = draftTo.service.canHaveAttachmentType(
    AttachmentTypes.LINK,
    draftTo.updateType,
  )

  return !draftFromCanHaveLink && draftToCanHaveLink
}

const getEnabledAttachmentTypeFromDraft = (
  draftFrom?: Draft,
  draftTo?: Draft,
): string | null => {
  if (!draftFrom || !draftTo) return null

  if (draftFrom.hasThread()) {
    return draftFrom.thread?.[0]?.enabledAttachmentType ?? null
  }

  if (
    !draftFrom.hasMediaAttachment() &&
    canEnableLinkAttachmentOnDraftCopy(draftFrom, draftTo)
  ) {
    return AttachmentTypes.LINK
  }

  return draftFrom.enabledAttachmentType
}

const copyDraftContents = ({
  draftFrom = ComposerStore.getDraft('omni'),
  draftsTo = state.drafts,
}: {
  draftFrom?: Draft
  draftsTo?: Draft[]
} = {}): void => {
  if (!draftFrom) return

  draftsTo.forEach((draft) => {
    const { service } = draft
    if (service.isOmni || draft === draftFrom) return

    clearDraft(draft.id, { preserveEnabledState: true })

    // Add text and data related to text
    copyDraftTextData(draftFrom, draft)

    draft.sourceLink = draftFrom.sourceLink
    draft.availableImages = draftFrom.availableImages

    // Sync shortening toggle with default settings of selected channels
    const profiles = AppStore.getSelectedProfilesForService(draft.service.name)
    // only enable if _all_ matching profiles have shortening enabled
    draft.shortenLinksToggle = profiles.every(
      (profile) =>
        profile.shorteningDomain !== LinkShorteningDomains.NoShortening,
    )

    // Force sync the settings
    ComposerActionCreators.updateRegenerateShortenedLinks(
      draft.id,
      draft.shortenLinksToggle,
    )

    /**
     * Below we add attachments. draftFromMedia is a structure created from the draftFrom,
     * or its active threaded draft (in case of Thread updates)
     * @type {{images: Image[], retweet: Retweet, gif: Gif, link: Link, enabledAttachmentType: string, videos: Video[]}}
     */
    const draftFromMedia = {
      enabledAttachmentType: getEnabledAttachmentTypeFromDraft(
        draftFrom,
        draft,
      ),

      link: draftFrom.hasThread() ? draftFrom.thread[0].link : draftFrom.link,
      retweet: draftFrom.hasThread()
        ? draftFrom.thread[0].retweet
        : draftFrom.retweet,
      videos: draftFrom.hasThread()
        ? draftFrom.thread[0].videos
        : draftFrom.videos,
      gif: draftFrom.hasThread() ? draftFrom.thread[0].gif : draftFrom.gif,
      images: draftFrom.hasThread()
        ? draft.service.supportsThread(draft.updateType)
          ? draftFrom.thread[0].images
          : draftFrom.thread.reduce<Image[]>(
              (allImages, threadedUpdate) =>
                allImages.concat(threadedUpdate.images),
              [],
            )
        : draftFrom.images,
      document: draftFrom?.document ? draftFrom.document : null,
    }

    if (draftFromMedia.enabledAttachmentType) {
      const draftUrl = draft.urls[0]
      switch (draftFromMedia.enabledAttachmentType) {
        case AttachmentTypes.LINK: {
          if (
            !service.canHaveAttachmentType(
              draftFromMedia.enabledAttachmentType,
              draft.updateType,
            )
          ) {
            const message = `${draft.service.formattedName} doesn't allow link attachments, so we removed it.`
            addOmniNotice(message, draft.id)
            return
          }

          if (
            !service.canEditLinkAttachment &&
            draftFromMedia.link?.wasEdited
          ) {
            const message = `${draft.service.formattedName} doesn't allow editing links, so we kept the original link attachment.`
            addOmniNotice(message, draft.id)
          }

          if (draftFromMedia.link !== null) {
            const linkDataToReuse =
              // If the service is facebook we only want to pass the URL so we
              // can check domain ownership before including the link attachment
              service.canEditLinkAttachment && service.name !== Service.Facebook
                ? {
                    url: draftFromMedia.link.url,
                    title: draftFromMedia.link.title,
                    description: draftFromMedia.link.description,
                    thumbnail:
                      draftFromMedia.link.thumbnail !== null
                        ? draftFromMedia.link.thumbnail
                        : null,
                  }
                : {
                    url: draftFromMedia.link.url,
                  }

            updateDraftLinkData(draft.id, linkDataToReuse, {
              isNewLinkAttachment: true,
              comesFromDirectUserAction: false,
            })
            enableDraftAttachment(draft.id, AttachmentTypes.LINK)
          } else if (draftUrl) {
            ComposerActionCreators.scrapeDraftLinkData(draft.id, draftUrl)
            enableDraftAttachment(draft.id, AttachmentTypes.LINK)
            ComposerActionCreators.toggleAttachment(
              draft.id,
              AttachmentTypes.LINK,
            )
          }

          break
        }

        case AttachmentTypes.MEDIA: {
          if (
            !service.canHaveAttachmentType(
              draftFromMedia.enabledAttachmentType,
              draft.updateType,
            )
          ) {
            const message = `${draft.service.formattedName} doesn't allow image attachments, so we removed it.`
            addOmniNotice(message, draft.id)
            return
          }

          const { videoMaxSize } = service
          if (
            videoMaxSize &&
            draftFromMedia.videos.some((video) => video.size > videoMaxSize)
          ) {
            const message = `${
              draft.service.formattedName
            } allows video size to be up to ${Math.round(
              videoMaxSize / 1024 / 1024,
            )}MB.`
            addOmniNotice(message, draft.id)
          }

          copyDraftMedia(draftFromMedia, draft)
          break
        }

        case AttachmentTypes.RETWEET: {
          if (
            !service.canHaveAttachmentType(
              draftFromMedia.enabledAttachmentType,
              draft.updateType,
            )
          ) {
            const message = `${draft.service.formattedName} doesn't allow retweet attachments, so we removed it.`
            addOmniNotice(message, draft.id)
            return
          }

          draft.retweet = draftFromMedia.retweet
          enableDraftAttachment(draft.id, AttachmentTypes.RETWEET)
          break
        }

        default:
          break
      }
    }
  })

  // Let draftFrom remain the last active draft, so that networks that are added
  // immediately after still use the same draft as reference (instead of a
  // possibly more restricted other network, e.g. Twitter)
  state.meta.lastInteractedWithComposerId = draftFrom.id

  // we need to set isTwitterPremium flag on twitter drafts for increased char limit
  // to work, as it checks draft.isTwitterPremium to determine it
  const twitterDraft = draftsTo?.find((draft) => draft.id === 'twitter')
  if (twitterDraft) {
    const isTwitterPremium = AppStore.getSelectedProfilesForService(
      'twitter',
    ).reduce((acc, profile) => acc && !!profile.isTwitterPremium, true)
    ComposerActionCreators.setIsTwitterPremium('twitter', isTwitterPremium)
  }

  const mastodonDraft = draftsTo?.find((draft) => draft.id === 'mastodon')
  if (mastodonDraft) {
    ComposerActionCreators.setMastodonMaxCharacters(
      'mastodon',
      AppStore.getSelectedMastodonMaxCharacters(),
    )
  }
}

const clearDraft = (
  id: string,
  { preserveEnabledState = false }: { preserveEnabledState?: boolean } = {},
): void => {
  const draft = getRequiredDraft(id)
  const emptyDraft = getNewDraft(draft.service)

  if (preserveEnabledState) emptyDraft.isEnabled = draft.isEnabled

  lastSyncedYoutubeTitlesByDraftId.delete(id)

  // Media fields must be written through the Draft mutation funnel (see
  // Draft.ts), so they are excluded from the generic Object.assign and
  // reset explicitly.
  const {
    images: _images,
    video: _video,
    videos: _videos,
    gif: _gif,
    document: _document,
    assets: _assets,
    ...restEmptyDraft
  } = emptyDraft

  Object.assign(draft, restEmptyDraft, { scheduledAt: draft.scheduledAt })
  draft.resetMedia()
}

const parseDraftsTextLinks = (): void => {
  const enabledDrafts = ComposerStore.getEnabledDrafts()
  enabledDrafts.forEach((draft) => parseDraftTextLinks(draft.id))
}

const insertHashtag = ({
  draft,
  text,
}: {
  draft: Draft
  text: string
}): void => {
  const { id, editorState } = draft
  const newEditorState = EditorStateProxy.insertSnippet(editorState, text)
  setDraftEditorState(id, newEditorState)
}

const insertHashtagInCaption = (text: string): void => {
  const { lastInteractedWithComposerId } = ComposerStore.getMeta()
  const draft = ComposerStore.getDraft(lastInteractedWithComposerId)

  if (draft) {
    insertHashtag({ draft, text })
  } else {
    const enabledDrafts = ComposerStore.getEnabledDrafts()
    enabledDrafts.forEach((enabledDraft) => {
      insertHashtag({ draft: enabledDraft, text })
    })
  }
}

const onDispatchedPayload = (
  payload: SynchronousComposerDispatchPayload,
): void => {
  const { action } = payload
  let shouldEmitChange = true
  let video: Video | null = null

  switch (action.actionType) {
    case ActionTypes.COMPOSER_SET_DRAFTS_INITIAL_TEXT:
      setDraftsInitialText({
        text: action.text,
        facebookMentionEntities: action.facebookMentionEntities,
        isEditing: action.isEditing,
        annotations: action.annotations,
      })
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_EDITOR_STATE:
      setDraftEditorState(action.id, action.editorState)
      break

    case ActionTypes.UPDATE_DRAFT_IS_SAVED:
      updateDraftIsSaved(action.id)
      break

    case ActionTypes.COMPOSER_HIDE_SUGGESTED_MEDIA:
      hideSuggestedMedia()
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFTS_UPDATE_TYPE:
      // The loaded post type is only meaningful for the network it came
      // from (e.g. Instagram's `reels`) — a network with no post types of
      // its own (Pinterest, Twitter, ...) has no valid representation for
      // it and should keep the default its draft was constructed with.
      state.drafts.forEach((draft) => {
        if (draft.service.allowedPostTypes.includes(action.updateType)) {
          updateDraftUpdateType(draft.id, action.updateType)
        }
      })
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_IS_REMINDER:
      updateDraftIsReminder(action.draftId, action.isReminder)
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFTS_IS_REMINDER:
      state.drafts.forEach((draft) =>
        ComposerActionCreators.updateDraftIsReminder(
          draft.id,
          action.isReminder,
        ),
      )
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_UPDATE_TYPE:
      updateDraftUpdateType(action.id, action.updateType)
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_SELECTED_STICKERS:
      updateDraftSelectedStickers(action.id, action.selectedStickers)
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFTS_INITIAL_STICKERS:
      state.drafts.forEach((draft) => updateDraftInitialStickers(draft.id))
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_GOOGLE_BUSINESS_DATA:
      updateDraftChannelData(SERVICE_GOOGLEBUSINESS, action.googleBusinessData)
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_MASTODON_DATA:
      updateDraftChannelData(SERVICE_MASTODON, action.mastodonData)
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_START_PAGE_DATA:
      updateDraftChannelData(SERVICE_STARTPAGE, action.startPageData)
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_INSTAGRAM_DATA:
      updateDraftChannelData(SERVICE_INSTAGRAM, action.instagramData)

      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_LINKEDIN_DATA:
      updateDraftChannelData(SERVICE_LINKEDIN, action.linkedinData)
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_FACEBOOK_DATA:
      updateDraftChannelData(SERVICE_FACEBOOK, action.facebookData)
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_YOUTUBE_DATA:
      updateDraftChannelData(SERVICE_YOUTUBE, action.youtubeData)
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_TIKTOK_DATA:
      updateDraftChannelData(SERVICE_TIKTOK, action.tiktokData)
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_TWITTER_DATA:
      updateDraftChannelData(SERVICE_TWITTER, action.twitterData)
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_THREADS_DATA:
      updateDraftChannelData(SERVICE_THREADS, action.threadsData)
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFTS_IMAGE_USER_TAGS:
      // need to add timeout because draft images doesn't load right away in edit mode
      setTimeout(() => {
        state.drafts.forEach((draft) =>
          updateDraftImageUserTags(draft.id, action && action.userTags),
        )
      })
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFTS_TITLE:
      state.drafts.forEach((draft) => updateDraftTitle(draft.id, action?.title))
      break

    case ActionTypes.COMPOSER_UPDATE_TOGGLE_SIDEBAR:
      updateToggleSidebarVisibility(action.id, action.composerSidebarVisible)
      break

    case ActionTypes.COMPOSER_UPDATE_SHOULD_SHORTEN_LINKS:
      updateShouldShortenLinks(action.id, action.shouldShortenLinks)
      break

    case ActionTypes.COMPOSER_UPDATE_REGENERATE_SHORTENED_LINKS:
      updateRegenerateShortenedLinks(action.id, action.shouldShortenLinks)
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_COMMENT_CHARACTER_COUNT:
      updateDraftCommentCharacterCount(action.id)
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_TITLE_CHARACTER_COUNT:
      updateDraftTitleCharacterCount(action.id)
      break

    case ActionTypes.COMPOSER_PARSE_DRAFT_TEXT_LINKS:
      parseDraftTextLinks(action.id)
      break

    case ActionTypes.COMPOSER_PARSE_MASTODON_TEXT:
      parseMastodonText()
      break

    case ActionTypes.COMPOSER_PARSE_DRAFTS_TEXT_LINKS:
      parseDraftsTextLinks()
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFTS_SCHEDULED_AT:
      state.drafts.forEach((draft) => {
        updateDraftScheduledAt(
          draft.id,
          action.scheduledAt,
          action.isPinnedToSlot,
        )
      })
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_LINK_DATA:
      updateDraftLinkData(action.id, action.linkData, action.meta)
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFTS_LINK_DATA:
      state.drafts.forEach((draft) =>
        updateDraftLinkData(draft.id, action.linkData, action.meta),
      )
      break

    case ActionTypes.COMPOSER_ADD_DRAFT_IMAGE:
      addDraftImage(action.id, action.image)
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_LINK_THUMBNAIL:
      updateDraftLinkThumbnail(action.id, action.thumbnail)
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_VIDEO_THUMBNAIL:
      updateDraftVideoThumbnail(action.id, action.thumbnail)
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_VIDEO_TITLE:
      updateDraftVideoTitle(action.id, action.title, action.video)
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_SOURCE_LINK:
      updateDraftSourceLink(action.id, action.url)
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFTS_SOURCE_LINK:
      state.drafts.forEach((draft) =>
        updateDraftSourceLink(draft.id, action.url),
      )
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_SOURCE_LINK_DATA:
      updateDraftSourceLinkData(action.id, action.linkData)
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_LIST_PLACES:
      updateDraftListPlaces(action.id, action.places)
      break

    case ActionTypes.COMPOSER_UPDATE_VIDEO_DRAFT_THUMBNAIL:
      updateVideoDraftThumbnail(
        action.id,
        action.thumbOffset,
        action.thumbnail,
        action.video,
      )
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_THUMBNAIL_GENERATED: {
      // No draft.video patch here: this is a video landing (new or
      // replacing), not an edit to one already attached — addDraftVideo
      // below gives it its own thumbnail. Patching draft.video (the first
      // getter) first, unconditionally, corrupted an unrelated already-
      // attached video's thumbnail on every subsequent upload in the same
      // batch — the reported "thumbnails swap" bug.
      const { videoMetadata } = action

      const processedVideoData = {
        uploadId: action.uploadId,
        name: action.name,
        duration: videoMetadata.duration,
        durationMs: videoMetadata.durationMs,
        frameRate: videoMetadata.frameRate,
        ...(videoMetadata.audioCodec !== undefined
          ? { audioCodec: videoMetadata.audioCodec }
          : {}),
        size: videoMetadata.size,
        width: videoMetadata.width,
        height: videoMetadata.height,
        url: videoMetadata.url,
        originalUrl: videoMetadata.url,
        thumbnail: action.thumbnailURL,
        availableThumbnails: [action.thumbnailURL],
        thumbOffset: 0,
        mediaType: MediaTypes.VIDEO,
      } satisfies Video

      // A draft whose service/mode can never take a video shouldn't leave
      // this upload resurfacing as a phantom suggestion.
      if (addDraftVideo(action.draftId, processedVideoData, true)) {
        addSharedUploadedVideo(processedVideoData)
      }

      break
    }

    case ActionTypes.COMPOSER_REMOVE_DRAFT_IMAGE:
      removeDraftImage(action.id, action.image)
      break

    case ActionTypes.COMPOSER_REMOVE_DRAFT_DOCUMENT:
      removeDraftDocument(action.id)
      break

    case ActionTypes.COMPOSER_ADD_DRAFT_VIDEO:
      addDraftVideo(action.id, action.video)
      break

    case ActionTypes.COMPOSER_ADD_DRAFT_GIF:
      addDraftGif(action.id, action.gif)
      break

    case ActionTypes.COMPOSER_REPLACE_DRAFT_GIF:
      shouldEmitChange = replaceDraftGif(
        action.draftId,
        action.expectedGif,
        action.replacement,
      )
      break

    case ActionTypes.COMPOSER_REMOVE_DRAFT_VIDEO:
      removeDraftVideo(action.id, action.video)
      break

    case ActionTypes.COMPOSER_REMOVE_DRAFT_GIF:
      removeDraftGif(action.id)
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_IMAGE_USER_TAGS:
      updateDraftImageUserTags(
        action.id,
        action && action.userTags,
        action.mediaUrl,
      )
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_IMAGE_USER_TAGS_ALL_DRAFTS:
      state.drafts.forEach((draft) =>
        updateDraftImageUserTags(
          draft.id,
          action && action.userTags,
          action.mediaUrl,
        ),
      )
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_TITLE:
      updateDraftTitle(action.id, action?.title)
      break

    case ActionTypes.COMPOSER_ADD_DRAFTS_RETWEET:
      state.drafts.forEach((draft) =>
        addDraftRetweet(draft.id, action.retweetData),
      )
      break

    case ActionTypes.COMPOSER_TOGGLE_DRAFT_ATTACHMENT:
      toggleDraftAttachment(action.id, action.attachmentType)
      break

    case ActionTypes.COMPOSER_DISABLE_DRAFT_ATTACHMENT:
      disableDraftsAttachment(action.id, action.attachmentType)
      break

    case ActionTypes.COMPOSER_ENABLE_DRAFTS_ATTACHMENT:
      state.drafts.forEach((draft) =>
        enableDraftAttachment(draft.id, action.attachmentType),
      )
      break

    case ActionTypes.COMPOSER_ENABLE:
      shouldEmitChange = enableDraft(action.id, action.markAppAsLoadedWhenDone)
      break

    case ActionTypes.COMPOSER_DISABLE:
      shouldEmitChange = disableDraft(action.id)
      break

    case ActionTypes.UPDATE_DRAFT_HAS_SAVING_ERROR:
      updateDraftHasSavingError(action.id, action.hasSavingError)
      break

    case ActionTypes.COMPOSER_CLEAR_INLINE_ERRORS:
      clearDraftInlineErrors(action.id)
      break

    case ActionTypes.COMPOSER_DRAFT_LINK_SHORTENED:
      mapShortLinkWithLongLink(action.id, action.shortLink, action.link)
      replaceDraftLinkWithShortlink(action.id, action.link, action.shortLink)
      break

    case ActionTypes.COMPOSER_DRAFT_LINK_UNSHORTENED:
      addDraftUnshortenedLink(action.id, action.unshortenedLink)
      parseDraftTextLinks(action.id)
      unmapShortLinkWithLongLink(action.id, action.shortLink)
      break

    case ActionTypes.COMPOSER_DRAFT_LINK_RESHORTENED:
      parseDraftTextLinks(action.id)
      mapShortLinkWithLongLink(action.id, action.shortLink, action.link)
      break

    case ActionTypes.COMPOSER_ADD_DRAFT_AVAILABLE_IMAGES:
      addDraftAvailableImages(action.id, action.images, action.sourceLink)
      break

    case ActionTypes.COMPOSER_REMOVE_DRAFT_AVAILABLE_IMAGES:
      removeDraftAvailableImages(action.id, action.sourceLink)
      break

    case ActionTypes.COMPOSER_ADD_DRAFT_UPLOADED_IMAGE:
      // Order matters, this should remain last
      addDraftUploadedImage({
        draftId: action.id,
        url: action.url,
        width: action.width,
        height: action.height,
        name: action.name,
        size: action.size,
        source: action.source,
        uploadId: action.uploadId,
      })
      break

    case ActionTypes.UPLOADED_LINK_THUMBNAIL:
      addDraftUploadedLinkThumbnail(
        action.id,
        action.url,
        action.width,
        action.height,
      )
      break

    case ActionTypes.COMPOSER_ADD_DRAFT_UPLOADED_VIDEO:
      state.draftsSharedData.processingVideos.set(action.uploadId, [action.id])
      break

    case ActionTypes.COMPOSER_ADD_DRAFT_UPLOADED_GIF:
      addDraftUploadedGif(
        action.id,
        action.url,
        action.stillGifUrl,
        action.width,
        action.height,
        action.frameCount,
      )
      break

    case ActionTypes.COMPOSER_SET_LINKEDIN_CAROUSEL_TITLE:
      const draft = ComposerStore.getDraft(SERVICE_LINKEDIN)

      if (!draft) return

      enableLinkedInDocument(draft)
      if (draft.document) {
        draft.document.title = action.title
      }

      break

    case ActionTypes.COMPOSER_ADD_DRAFT_UPLOADED_DOCUMENT:
      const draftIds = action.draftId
        ? [action.draftId]
        : state.drafts.map((draftItem) => draftItem.id)

      draftIds.forEach((draftId) => {
        addDraftDocument(draftId, action.document)
      })

      break

    case ActionTypes.COMPOSER_VIDEO_PROCESSED: {
      const videoData = action.processedVideoData
      const { processingVideos } = state.draftsSharedData

      const processingDraftIds = processingVideos.get(videoData.uploadId)
      if (!processingDraftIds) return

      const [draftId] = processingDraftIds
      const newVideo = getNewVideo(videoData)

      if (addDraftVideo(draftId, newVideo, true)) {
        addSharedUploadedVideo(newVideo)
      }

      break
    }

    case ActionTypes.COMPOSER_ADD_DRAFTS_AUTO_UPLOADED_IMAGE:
      addAutoUploadedImage(
        action.url,
        action.altText,
        action.source,
        action.height,
        action.width,
      )
      break

    case ActionTypes.COMPOSER_ADD_DRAFTS_AUTO_UPLOADED_GIF:
      addAutoUploadedGif(action.url, action.stillGifUrl)
      break

    case ActionTypes.COMPOSER_UPDATE_NEXT_LINK_THUMBNAIL:
      selectNextLinkThumbnail(action.draftId)
      break

    case ActionTypes.COMPOSER_UPDATE_PREVIOUS_LINK_THUMBNAIL:
      selectPreviousLinkThumbnail(action.draftId)
      break

    case ActionTypes.COMPOSER_UPDATE_IMAGE_ALT_TEXT:
      updateImageAltText(action.image, action.altText)
      break

    case ActionTypes.COMPOSER_APPLY_IMAGE_WORKSPACE_CHANGES:
      applyImageWorkspaceChanges(action.draftId, action.changes)
      break

    case ActionTypes.COMPOSER_REPLACE_DRAFT_IMAGES:
      replaceDraftImages(action.draftId, action.oldUrls, action.images)
      break

    case ActionTypes.COMPOSER_SET_IMAGE_BEING_REPLACED:
      setImageBeingReplaced({
        imageUrl: action.imageUrl,
        isBeingReplaced: action.isBeingReplaced,
        serviceId: action.serviceId,
      })
      break

    case ActionTypes.COMPOSER_UPDATE_UPLOADED_IMAGE_DIMENSIONS:
      updateUploadedImageDimensions(action.url, action.width, action.height)

      break

    case ActionTypes.COMPOSER_APPLY_OMNI_UPDATE:
      if (AppStore.getSelectedProfiles().length === 0) {
        shouldEmitChange = false
        break
      }
      copyDraftContents()
      AppActionCreators.updateOmniboxState(false)
      break

    // Same content copy as COMPOSER_APPLY_OMNI_UPDATE, but stays in omni mode.
    // In omni mode the omni draft is the source of truth; the per-channel drafts
    // hold stale content from channel-enable time. The save path reads the
    // per-channel drafts, so it flushes omni content onto them first.
    case ActionTypes.COMPOSER_APPLY_OMNI_CONTENT:
      if (AppStore.getSelectedProfiles().length === 0) {
        shouldEmitChange = false
        break
      }
      copyDraftContents()
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFTS_THREAD:
      state.drafts.forEach((draftToUpdate) =>
        updateDraftThread(draftToUpdate.id, action.thread),
      )
      break

    case ActionTypes.COMPOSER_UPDATE_AI_ASSISTED:
      state.drafts.forEach((draftToUpdate) =>
        updateAiAssisted(draftToUpdate.id),
      )
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_ADD_POST_TO_THREAD:
      updateDraftAddPostToThread(action.id, action.offset)
      break

    case ActionTypes.COMPOSER_UPDATE_POST_TEXT:
      updateDraftPostText(action.id, action.text)
      break

    case ActionTypes.COMPOSER_SET_ACTIVE_THREAD_ID:
      setActiveThreadId(action.threadId)
      break

    case ActionTypes.COMPOSER_SWITCH_ACTIVE_THREAD_EDITOR:
      switchActiveThreadEditor(action.id, action.threadId)
      break

    case ActionTypes.COMPOSER_DELETE_THREADED_DRAFT:
      deleteThreadedDraft(action.id, action.threadId, action.mergeThreads)
      break

    case ActionTypes.COMPOSER_UPDATE_THREAD_ORDER:
      updateDraftThreadOrder(action.id, action.newThreadOrder)
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_MEDIA_ORDER:
      updateDraftMediaOrder(action.id, action.dragIndex, action.hoverIndex)
      break

    case ActionTypes.COMPOSER_ADD_DRAFTS_AUTO_UPLOADED_VIDEO:
      video = getNewVideo(action.video)
      addSharedUploadedVideo(video)
      state.drafts.forEach((videoDraft) => addDraftVideo(videoDraft.id, video))
      break

    // Used when every video of an existing post is already known up front
    // (editing an existing multi-video post) — see loadDraftVideos.
    case ActionTypes.COMPOSER_ADD_DRAFTS_AUTO_UPLOADED_VIDEOS: {
      const videos = (action.videos as Video[]).map(getNewVideo)
      videos.forEach((v) => addSharedUploadedVideo(v))
      state.drafts.forEach((videoDraft) =>
        loadDraftVideos(videoDraft.id, videos),
      )
      break
    }

    case ActionTypes.COMPOSER_RESTORE_DRAFTS_MEDIA_ORDER:
      state.drafts.forEach((draftToUpdate) =>
        draftToUpdate.reorderMedia(action.order),
      )
      break

    case ActionTypes.COMPOSER_DRAFTS_PREVENT_AUTO_ATTACHING_URLS:
      state.drafts.forEach((draftToUpdate) => {
        draftToUpdate.urls = action.urls
      })
      break

    case ActionTypes.APP_RESET:
      // Delegate to resetState so both paths share the same cleanup,
      // including clearing the getEnabledDrafts memo.
      ComposerStore.resetState()
      break

    case ActionTypes.COMPOSER_INSERT_HASHTAG_TO_CAPTION:
      insertHashtagInCaption(action.text)
      break

    case ActionTypes.COMPOSER_SET_IS_TWITTER_PREMIUM:
      const twitterDraft = ComposerStore.getDraft(action.id)
      if (!twitterDraft) return
      twitterDraft.isTwitterPremium = action.isTwitterPremium
      break

    case ActionTypes.COMPOSER_SET_MASTODON_MAX_CHARACTERS:
      const mastodonDraft = ComposerStore.getDraft(action.id)
      if (!mastodonDraft) return
      mastodonDraft.mastodonMaxCharacters = action.maxCharacters
      break

    case ActionTypes.COMPOSER_UPDATE_DRAFT_GIF_FRAME_COUNT:
      const draftToUpdate = ComposerStore.getDraft(action.id)
      if (draftToUpdate?.gif) {
        draftToUpdate.gif.frameCount = action.frameCount
      }
      break

    default:
      shouldEmitChange = false
      break
  }

  if (shouldEmitChange) ComposerStore.emitChange()

  // (uncomment to enable Redux DevTools)
  // sendToMonitor('composer', action, state);
}

AppDispatcher.register((payload) =>
  onDispatchedPayload({
    action: Object.assign({}, payload.action, {
      actionType: payload.action.actionType,
    }),
  }),
)

export default ComposerStore
